// main.js is now an ES module — loaded via <script type="module">.
// Imports from @theme/* (resolved via the importmap in theme.liquid).
// `secondary.js` imports from `@theme/main` for the shared helpers
// `trapFocus`, `removeTrapFocus`, `debounce`, `pauseAllMedia`.
import { Component } from "@theme/component";
import { morph, MORPH_OPTIONS } from "@theme/morph";
import { sectionRenderer, buildSectionSelector } from "@theme/section-renderer";
// Helpers sourced from utilities.js so our theme actually uses what we ship.
// Re-exported below so existing `import { … } from "@theme/main"` callers
// (secondary.js, media-gallery.js, facets.js, cart-notification.js,
// pickup-availability.js) keep importing from `@theme/main` unchanged.
// The remaining locally-defined helpers (trapFocus, removeTrapFocus,
// pauseAllMedia, getFocusableElements, onKeyUpEscape) stay defined here —
// Horizon's utilities.js doesn't have counterparts (focus-trap lives in
// its own Horizon module we haven't ported).
import {
	debounce,
	fetchConfig,
	prefersReducedMotion,
	throttle,
	clamp,
	center,
	onAnimationEnd,
	isClickedOutside,
} from "@theme/utilities";
export { debounce, fetchConfig, prefersReducedMotion, throttle, clamp, center, onAnimationEnd, isClickedOutside };

const currentDate = new Date();

// ----------------------------------------------------------------------
// Cross-component event system
// ----------------------------------------------------------------------
// Replaces the older publish/subscribe pub-sub pattern with native
// CustomEvent subclasses dispatched on `document`. Benefits over pub/sub:
//   - Events appear in DevTools' Performance / Event Listeners panels.
//   - `addEventListener` integrates with AbortController for clean removal.
//   - Listeners can be attached anywhere in the DOM tree, not just to
//     module-scoped subscriber arrays.
//
// Event names live on ThemeEvents.* so callers don't hard-code strings.
//
// `publish` and `subscribe` are kept as thin compatibility shims for any
// inline-liquid/app code that might still call them. New code should use
// `document.dispatchEvent(new XYZEvent(detail))` and
// `document.addEventListener(ThemeEvents.xxx, handler, { signal })` directly.

const ThemeEvents = {
	cartUpdate: "cart:update",
	variantChange: "variant:change",
	quantityUpdate: "quantity:update",
};

class CartUpdateEvent extends CustomEvent {
	constructor(detail) {
		super(ThemeEvents.cartUpdate, { detail, bubbles: false });
	}
}
class VariantChangeEvent extends CustomEvent {
	constructor(detail) {
		super(ThemeEvents.variantChange, { detail, bubbles: false });
	}
}
class QuantityUpdateEvent extends CustomEvent {
	constructor(detail) {
		super(ThemeEvents.quantityUpdate, { detail, bubbles: false });
	}
}
// Animate the cart count bubble whenever the cart-icon-bubble section is
// re-rendered (after Add-to-Cart, quantity changes from the cart page,
// cross-tab updates, etc.). A MutationObserver covers every callsite
// uniformly without having to hook each one. The CSS rule for the
// `.is-animating` class lives in base.css.
(function setupCartBubbleAnimation() {
	const cartIconBubble = document.getElementById("cart-icon-bubble");
	if (!cartIconBubble) return;
	const animateBubble = () => {
		const bubble = cartIconBubble.querySelector(".cart-count-bubble, .bag3__count");
		if (!bubble) return;
		// Restart the animation: remove the class, force a reflow, re-add.
		// Without the reflow the browser doesn't see a transition and skips
		// re-running the animation if the class is already applied.
		bubble.classList.remove("is-animating");
		void bubble.offsetWidth;
		bubble.classList.add("is-animating");
	};
	new MutationObserver(animateBubble).observe(cartIconBubble, {
		childList: true,
		subtree: true,
		// Tier 2 morphing updates text nodes in place (e.g. count "1" → "2"),
		// which fires characterData mutations rather than childList. Without
		// this flag the bubble pop would silently skip on count-only changes.
		characterData: true,
	});
})();

class ContactOptions extends Component {
	#availabilitySet = false;
	constructor() {
		super();

		this.fromDayAvailability = parseInt(this.dataset.fromDayAvailability); // e.g., 0
		this.toDayAvailability = parseInt(this.dataset.toDayAvailability); // e.g., 4
		this.fromHourAvailability = parseInt(this.dataset.fromHourAvailability); // e.g., 9
		this.toHourAvailability = parseInt(this.dataset.toHourAvailability); // e.g., 17
		this.outOfServiceText = this.dataset.outOfServiceText; // e.g., "Out of service"
		this.fromToAvailabilityText = this.dataset.fromToAvailabilityText; // e.g., "From Monday to Friday from 10:00 to 18:00."

		this.updateAvailability();
	}
	updateAvailability() {
		if (this.#availabilitySet) return;

		const contactItem = this.querySelector(".contact-item");
		if (!contactItem) return;
		const statusLabel = this.querySelector(".contact-item__label");
		if (!statusLabel) return;

		// Check if current hours and days are within the availability range
		const date = new Date();
		const currentDay = date.getDay(); // 0 (Sunday) to 6 (Saturday)
		const currentHour = date.getHours(); // 0 to 23
		date.setMinutes(0);

		let isAvailable = false;
		if (currentDay >= this.fromDayAvailability && currentDay <= this.toDayAvailability) {
			if (currentHour >= this.fromHourAvailability && currentHour < this.toHourAvailability) {
				isAvailable = true;
			}
		}

		if (!isAvailable) {
			contactItem.setAttribute("data-state", "out-of-service");
			statusLabel.textContent = this.outOfServiceText;
		}

		const contactDescription = this.querySelectorAll(".contact-item__description")?.[0];
		if (contactDescription) {
			const dateFormat = new Intl.DateTimeFormat(`${window.Shopify.locale}-${window.Shopify.country}`, { weekday: "long" });
			const fromDayText = dateFormat.format(new Date().setDate(date.getDate() + (this.fromDayAvailability - currentDay)));
			this.fromToAvailabilityText = this.fromToAvailabilityText.replace("[from_day]", fromDayText);
			const toDayText = dateFormat.format(new Date().setDate(date.getDate() + (this.toDayAvailability - currentDay)));
			this.fromToAvailabilityText = this.fromToAvailabilityText.replace("[to_day]", toDayText);

			const timeFormat = new Intl.DateTimeFormat(`${window.Shopify.locale}-${window.Shopify.country}`, { hour: "numeric", minute: "numeric" });
			const fromHourText = timeFormat.format(new Date(date.setHours(this.fromHourAvailability)));
			this.fromToAvailabilityText = this.fromToAvailabilityText.replace("[from_hour]", fromHourText);
			const toHourText = timeFormat.format(new Date(date.setHours(this.toHourAvailability)));
			this.fromToAvailabilityText = this.fromToAvailabilityText.replace("[to_hour]", toHourText);

			contactDescription.textContent = this.fromToAvailabilityText;
		}

		this.#availabilitySet = true;

		// Show the element now that availability has been set
		this.style.display = "block";
	}
}
customElements.define("contact-options", ContactOptions);

class AsSeenOn extends Component {
	#countSet = false;
	#rafId = null;

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
	}
	constructor() {
		super();

		this.text = this.dataset.text;
		this.counter = parseFloat(this.dataset.counter);
		this.duration = parseInt(this.dataset.duration);

		this.insertCount();
	}
	insertCount() {
		if (this.#countSet) return;

		const counter = document.getElementById("counter");
		const finalValue = this.counter;
		const duration = this.duration;
		let startTime = null;

		// easing function (easeOutQuad)
		function easeOutQuad(t) {
			return 1 - (1 - t) * (1 - t);
		}

		const self = this;
		function animateCounter(timestamp) {
			if (!startTime) startTime = timestamp;
			const elapsed = timestamp - startTime;
			const t = Math.min(elapsed / duration, 1); // normalize [0..1]

			const easedProgress = easeOutQuad(t);
			const currentValue = finalValue * easedProgress;

			counter.textContent = currentValue.toFixed(1);
			if (t < 1) {
				self.#rafId = requestAnimationFrame(animateCounter);
			} else {
				self.#rafId = null;
			}
		}

		this.#rafId = requestAnimationFrame(animateCounter);

		this.#countSet = true;
	}
}
customElements.define("as-seen-on", AsSeenOn);

class BoughtCount extends Component {
	#countSet = false;
	constructor() {
		super();

		this.text = this.dataset.text;
		this.count = parseInt(this.dataset.baseCount);
		this.hourMultiplier = parseInt(this.dataset.hourMultiplier);
		this.minuteMultiplier = parseInt(this.dataset.minuteMultiplier);
		this.insertCount();
	}
	insertCount() {
		if (this.#countSet) return;

		// Set the tens based on the current hour
		// and the ones based on the tens of minute
		let text = this.text;
		if (text.includes("[base_count]")) {
			const now = new Date();
			const options = {
				timeZone: "Europe/Paris",
				hour: "2-digit",
				minute: "2-digit",
				hour12: false,
			};

			const frenchTime = new Intl.DateTimeFormat("fr-FR", options).formatToParts(now).reduce((acc, part) => {
				if (part.type === "hour") acc.hour = parseInt(part.value, 10);
				if (part.type === "minute") acc.minute = parseInt(part.value, 10);
				return acc;
			}, {});

			const hour = frenchTime.hour * this.hourMultiplier; // e.g., 22
			const minutes = frenchTime.minute * this.minuteMultiplier; // e.g., 47

			// Only grab the first digit of the minutes
			const firstMinute = parseInt(minutes.toString()[0], 10);
			const finalCount = this.count + hour + firstMinute;
			text = text.replace("[base_count]", finalCount);
		}

		this.text = text;
		this.children[0].innerHTML = text;
		this.#countSet = true;

		// Show the element now that availability has been set
		this.style.display = "block";
	}
}
customElements.define("bought-count", BoughtCount);

class AnnouncementBarDate extends Component {
	#dateSet = false;
	constructor() {
		super();
		this.text = this.dataset.text;
		this.year = this.dataset.dateYear;
		this.month = this.dataset.dateMonth;
		this.day = this.dataset.dateDay;
		this.dateUppercase = this.dataset.dateUppercase === "true";
		this.insertDates();
	}
	insertDates() {
		if (this.#dateSet) return;

		let text = this.text;
		let endDate = null;
		if (text.includes("[end_date]")) {
			endDate = new Date();
			let endDateWeek = endDate.toLocaleDateString(window.Shopify.locale + "-" + window.Shopify.country, { day: "numeric", month: "long" });
			if (this.dateUppercase) endDateWeek = endDateWeek.toUpperCase();
			text = text.replace("[end_date]", endDateWeek);
		}

		if (text.includes("[start_date]")) {
			const startDate = new Date();
			startDate.setTime(Date.parse(`${this.month}/${this.day}/${this.year}`));

			const options = { day: "numeric" };
			if (endDate != null) {
				// getFullYear (not the deprecated getYear, which returns years-since-1900).
				const isSameYear = endDate.getFullYear() === startDate.getFullYear();
				const isSameMonth = endDate.getMonth() === startDate.getMonth();
				if (!isSameYear || !isSameMonth) {
					options.month = "long";
				}
			}

			let startDateWeek = startDate.toLocaleDateString(window.Shopify.locale + "-" + window.Shopify.country, options);
			if (this.dateUppercase) startDateWeek = startDateWeek.toUpperCase();

			text = text.replace("[start_date]", startDateWeek);
		}

		this.text = text;
		this.children[0].innerHTML = text;
		this.#dateSet = true;

		// Show the element now that availability has been set
		this.style.display = "block";
	}
}
customElements.define("announcement-bar-date", AnnouncementBarDate);

class CartRemoveButton extends Component {
	constructor() {
		super();
		this.addEventListener("click", (event) => {
			event.preventDefault();
			const cartItems = this.closest("cart-items") || this.closest("cart-drawer-items");
			if (this.clearCart) {
				cartItems.clearCart();
			} else {
				cartItems.updateQuantity(this.dataset.index, 0);
			}
		});
	}
}
customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends Component {
	constructor() {
		super();
		this.lineItemStatusElement = document.getElementById("shopping-cart-line-item-status") || document.getElementById("CartDrawer-LineItemStatus");
		this.secondCartItems = document.querySelector("cart-drawer-items");
		const debouncedOnChange = debounce((event) => {
			this.onChange(event);
		}, 300);
		this.addEventListener("change", debouncedOnChange.bind(this));
	}
	#abortController = new AbortController();
	connectedCallback() {
		super.connectedCallback();
		document.addEventListener(
			ThemeEvents.cartUpdate,
			(event) => {
				if (event.detail?.source === "cart-items") return;
				this.onCartUpdate();
			},
			{ signal: this.#abortController.signal }
		);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	onChange(event) {
		this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute("name"));
	}
	onCartUpdate() {
		// Cart state just changed: bypass cache to fetch fresh HTML.
		sectionRenderer
			.getSectionHTML("main-cart-items", false, new URL("/cart", window.location.origin))
			.then((text) => {
				const parsedDoc = new DOMParser().parseFromString(text, "text/html");
				const cartItemsEl = parsedDoc.querySelector("cart-items");
				if (!cartItemsEl) return;
				// Morph (childrenOnly) instead of `this.innerHTML = …` so
				// focused inputs and in-progress animations on cart lines
				// survive the refresh, and any Component children get their
				// `updatedCallback()` lifecycle hook fired.
				morph(this, cartItemsEl, { ...MORPH_OPTIONS, childrenOnly: true });
			})
			.catch((err) => {
				console.error(err);
			});
	}
	updateCart() {
		const requestBody = JSON.stringify({
			"sections": this.getSectionsToRender().map((section) => section.section),
			"sections_url": window.location.pathname,
		});
		fetch("/cart/update.js", {
			"method": "POST",
			"headers": {
				"Content-Type": "application/json",
			},
			"body": requestBody,
		})
			.then((res) => res.json())
			.then((data) => {
				this.classList.toggle("is-empty", data.item_count === 0);
				this.getSectionsToRender().forEach((section) => {
					const targetEl = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
					morphSectionContent(targetEl, data.sections[section.section], section.selector, section.useHydration);
				});
				bindPSPbtn();
			})
			.catch((err) => {
				console.error(err);
			});
	}
	getSectionsToRender() {
		return [
			{
				"id": "main-cart-items",
				"section": document.getElementById("main-cart-items").dataset.id,
				"selector": ".js-contents",
			},
			{
				"id": "cart-icon-bubble",
				"section": "cart-icon-bubble",
				"selector": ".shopify-section",
			},
			{
				"id": "cart-live-region-text",
				"section": "cart-live-region-text",
				"selector": ".shopify-section",
			},
			{
				"id": "main-cart-footer",
				"section": document.getElementById("main-cart-footer").dataset.id,
				"selector": ".js-contents",
			},
			// Keep the cart drawer in sync when /cart page actions fire. Without
			// this entry, +/- on the /cart page updates the page + cart-icon-bubble
			// but leaves the drawer (rendered in theme.liquid header on every
			// page) showing stale items — visible the moment the customer
			// opens it after the update. Hydration mode reuses the same keyed
			// elements we already added in Phase 3.
			{
				"id": "CartDrawer",
				"section": "cart-drawer",
				"selector": ".drawer__inner",
				"useHydration": true,
			},
		];
	}
	async updateQuantity(line, quantity, activeName) {
		this.enableLoading(line);
		const body = JSON.stringify({
			"line": line,
			"quantity": quantity,
			"sections": this.getSectionsToRender().map((section) => section.section),
			"sections_url": window.location.pathname,
		});
		try {
			const res = await fetch(routes.cart_change_url, {
				...fetchConfig(),
				...{
					body,
				},
			});
			const data = await res.json();

			const quantityInput = document.getElementById("Quantity-" + line) || document.getElementById("Drawer-quantity-" + line);
			const cartItemEls = document.querySelectorAll(".cart-item");
			if (data.errors) {
				quantityInput.value = quantityInput.getAttribute("value");
				this.updateLiveRegions(line, data.errors);
				return;
			}

			if (!data.sections) {
				const sectionsRes = await fetch(
					routes.cart_url +
						"?sections=" +
						this.getSectionsToRender()
							.map((section) => section.section)
							.join(","),
				);

				data.sections = await sectionsRes.json();
			}

			this.classList.toggle("is-empty", data.item_count === 0);

			const cartDrawer = document.querySelector("cart-drawer");
			const cartFooter = document.getElementById("main-cart-footer");
			if (cartFooter) {
				cartFooter.classList.toggle("is-empty", data.item_count === 0);
			}
			if (cartDrawer) {
				cartDrawer.classList.toggle("is-empty", data.item_count === 0);
			}
			this.getSectionsToRender().forEach((section) => {
				const targetEl = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
				morphSectionContent(targetEl, data.sections[section.section], section.selector, section.useHydration);
			});

			bindPSPbtn();

			const newQuantity = data.items[line - 1] ? data.items[line - 1].quantity : undefined;
			let errorMessage = "";
			if (cartItemEls.length === data.items.length && newQuantity !== parseInt(quantityInput.value)) {
				if (typeof newQuantity === "undefined") {
					errorMessage = window.cartStrings.error;
				} else {
					errorMessage = window.cartStrings.quantityError.replace("[quantity]", newQuantity);
				}
			}

			this.updateLiveRegions(line, errorMessage);

			const lineItem = document.getElementById("CartItem-" + line) || document.getElementById("CartDrawer-Item-" + line);
			if (lineItem && lineItem.querySelector('[name="' + activeName + '"]')) {
				if (cartDrawer) {
					trapFocus(cartDrawer, lineItem.querySelector('[name="' + activeName + '"]'));
				} else {
					lineItem.querySelector('[name="' + activeName + '"]').focus();
				}
			} else {
				if (data.item_count === 0 && cartDrawer) {
					const emptyEl = cartDrawer.querySelector(".drawer__inner-empty");
					const emptyAnchor = cartDrawer.querySelector("a");
					if (emptyEl && emptyAnchor) trapFocus(emptyEl, emptyAnchor);
				} else if (document.querySelector(".cart-item") && cartDrawer) {
					trapFocus(cartDrawer, document.querySelector(".cart-item__name"));
				}
			}

			if (cartDrawer) {
				cartDrawer.checkForClear();
				// (countdown-timer restart used to live here. It's now driven
				// by CountdownTimer#morphedCallback when the timer's DOM is
				// morphed by morphSectionContent above.)

				if (cartDrawer.querySelector("cart-drawer-gift")) {
					cartDrawer.checkForClear();
					let giftsToAdd = [];
					let giftsToRemove = [];
					cartDrawer.querySelectorAll("cart-drawer-gift").forEach((gift) => {
						if (gift.getUpdateRequired()) {
							if (cartDrawer.querySelector(".cart-item--product-" + gift.dataset.handle)) {
								if (gift.dataset.selected === "false") {
									giftsToRemove.push(gift);
								}
							} else if (gift.dataset.selected === "true") {
								giftsToAdd.push(gift);
							}
						}
					});

					if (giftsToRemove.length > 0) {
						giftsToRemove[0].removeFromCart();
					} else if (giftsToAdd.length > 0) {
						giftsToAdd[0].addToCart();
					}
				}
			}

			for (let i = 0; i < data.items_added.length; i++) {
				triggerAddToCartEvent(data.items_added[i]);
			}

			document.dispatchEvent(new CartUpdateEvent({ source: "cart-items" }));
		} catch (err) {
			this.querySelectorAll(".loading-overlay").forEach((overlay) => overlay.classList.add("hidden"));
			const errorsEl = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
			errorsEl.textContent = window.cartStrings.error;
		} finally {
			this.disableLoading(line);
			if (this.secondCartItems && this.secondCartItems.updateCart) {
				this.secondCartItems.updateCart();
			}
			const cartQtyEvent = new CustomEvent("cartQuantityUpdated", {
				"detail": {
					"handle": "handle",
				},
			});
			document.dispatchEvent(cartQtyEvent);
		}
	}
	updateLiveRegions(line, message) {
		const lineErrorEl = document.getElementById("Line-item-error-" + line) || document.getElementById("CartDrawer-LineItemError-" + line);
		if (lineErrorEl) {
			lineErrorEl.querySelector(".cart-item__error-text").innerHTML = message;
		}
		this.lineItemStatusElement.setAttribute("aria-hidden", true);
		const liveRegion = document.getElementById("cart-live-region-text") || document.getElementById("CartDrawer-LiveRegionText");
		liveRegion.setAttribute("aria-hidden", false);
		setTimeout(() => {
			liveRegion.setAttribute("aria-hidden", true);
		}, 1000);
	}
	enableLoading(line) {
		const itemsEl = document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
		itemsEl.classList.add("cart__items--disabled");
		const cartOverlays = this.querySelectorAll("#CartItem-" + line + " .loading-overlay");
		const drawerOverlays = this.querySelectorAll("#CartDrawer-Item-" + line + " .loading-overlay");
		[...cartOverlays, ...drawerOverlays].forEach((overlay) => overlay.classList.remove("hidden"));
		document.activeElement.blur();
		this.lineItemStatusElement.setAttribute("aria-hidden", false);
	}
	disableLoading(line) {
		const itemsEl = document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
		itemsEl.classList.remove("cart__items--disabled");
		const cartOverlays = this.querySelectorAll("#CartItem-" + line + " .loading-overlay");
		const drawerOverlays = this.querySelectorAll("#CartDrawer-Item-" + line + " .loading-overlay");
		cartOverlays.forEach((overlay) => overlay.classList.add("hidden"));
		drawerOverlays.forEach((overlay) => overlay.classList.add("hidden"));
	}
	clearCart() {
		const body = JSON.stringify({
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});
		fetch("" + routes.cart_clear_url, {
			...fetchConfig(),
			...{
				body,
			},
		})
			.then((response) => {
				return response.text();
			})
			.then((text) => {
				const data = JSON.parse(text);
				this.classList.add("is-empty");
				const cartDrawer = document.querySelector("cart-drawer");
				const cartFooter = document.getElementById("main-cart-footer");
				if (cartFooter) {
					cartFooter.classList.add("is-empty");
				}
				if (cartDrawer) {
					cartDrawer.classList.add("is-empty");
				}
				this.getSectionsToRender().forEach((section) => {
					const targetEl = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
					morphSectionContent(targetEl, data.sections[section.section], section.selector, section.useHydration);
				});
				if (cartDrawer) {
					trapFocus(cartDrawer.querySelector(".drawer__inner-empty"), cartDrawer.querySelector("a"));
				}

				bindPSPbtn();
				document.dispatchEvent(new CartUpdateEvent({ source: "cart-items" }));
			})
			.catch(() => {
				this.querySelectorAll(".loading-overlay").forEach((overlay) => overlay.classList.add("hidden"));
				const errorsEl = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
				errorsEl.textContent = window.cartStrings.error;
			});
	}
}
customElements.define("cart-items", CartItems);

var search = "search";
class CartNote extends Component {
	constructor() {
		super();
		this.addEventListener("change",
			debounce((event) => {
				const body = JSON.stringify({
					note: event.target.value,
				});
				fetch("" + routes.cart_update_url, {
					...fetchConfig(),
					...{
						body,
					},
				});
			}, 300),
		);
	}
}
if (!customElements.get("cart-note")) {
	customElements.define("cart-note", CartNote);
}

function handleDiscountForm(ev) {
	ev.preventDefault();

	const discountField = ev.target.querySelector("[name=cart-discount-field]");
	const discountError = ev.target.querySelector(".cart-discount-form__error");
	if (!discountField) return;
	const discountCode = discountField.value;
	if (!discountCode || !discountCode.length) {
		if (discountError) discountError.style.display = "block";
		return;
	}

	if (discountError) discountError.style.display = "none";
	if (window.Shopify && window.Shopify.submitCart) {
		if (!window.Shopify.skipCheckout) {
			window.Shopify.submitCart();
		}

		if (window.Shopify.skipCheckout) {
			window.Shopify.skipCheckout = false;
		}
	} else {
		const url = "/checkout?discount=" + discountCode;
		window.location.href = url;
	}
}

function handleDiscountFormChange(ev) {
	const allDiscountError = document.querySelectorAll(".cart-discount-form__error");
	allDiscountError.forEach((discountError) => {
		discountError.style.display = "none";
	});
}

var serial = "";
class SearchForm extends Component {
	constructor() {
		super();
		this.input = this.querySelector('input[type="search"]');
		this.resetButton = this.querySelector('button[type="reset"]');
		if (this.dataset.main === "false") {
			serial = this.querySelector('[method="get"]').dataset["nodal".replace("n", "m")];
		}
		if (this.input) {
			this.input.form.addEventListener("reset", this.onFormReset.bind(this));
			this.input.addEventListener(
				"input",
				debounce((event) => {
					this.onChange(event);
				}, 300).bind(this),
			);
		}
	}
	toggleResetButton() {
		const isHidden = this.resetButton.classList.contains("hidden");
		if (this.input.value.length > 0 && isHidden) {
			this.resetButton.classList.remove("hidden");
		} else if (this.input.value.length === 0 && !isHidden) {
			this.resetButton.classList.add("hidden");
		}
	}
	onChange() {
		this.toggleResetButton();
	}
	shouldResetForm() {
		return !document.querySelector('[aria-selected="true"] a');
	}
	onFormReset(event) {
		event.preventDefault();
		if (this.shouldResetForm()) {
			this.input.value = "";
			this.input.focus();
			this.toggleResetButton();
		}
	}
}
customElements.define("search-form", SearchForm);
class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
		this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.setupEventListeners();
	}
	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		this.addEventListener("keydown", this.onKeydown.bind(this));
	}
	getQuery() {
		return this.input.value.trim();
	}
	onChange() {
		super.onChange();
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);
		this.searchTerm = newSearchTerm;
		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}
		this.getSearchResults(this.searchTerm);
	}
	onFormSubmit(event) {
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
			event.preventDefault();
		}
	}
	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}
	onFocus() {
		const query = this.getQuery();
		if (!query.length) {
			return;
		}
		if (this.searchTerm !== query) {
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}
	onFocusOut() {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) {
				this.close();
			}
		});
	}
	onKeyup(event) {
		if (!this.getQuery().length) {
			this.close(true);
		}
		event.preventDefault();
		switch (event.code) {
			case "ArrowUp":
				this.switchOption("up");
				break;
			case "ArrowDown":
				this.switchOption("down");
				break;
			case "Enter":
				this.selectOption();
				break;
		}
	}
	onKeydown(event) {
		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.preventDefault();
		}
	}
	updateSearchForTerm(previousTerm, newTerm) {
		const searchForEl = this.querySelector("[data-predictive-search-search-for-text]");
		const searchForText = searchForEl?.innerText;
		if (!searchForText || !previousTerm) return;
		// `match()` returns null when there are no occurrences. The original
		// code did `.match(...).length` and threw `Cannot read properties of
		// null (reading 'length')` whenever previousTerm wasn't found in the
		// current text (e.g. the search-for hint was already updated by a
		// previous keystroke before this one's debounced handler ran).
		// Also escape regex specials so typing `(`, `[`, `.` etc. doesn't
		// throw `SyntaxError: Invalid regular expression`.
		const escaped = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const matches = searchForText.match(new RegExp(escaped, "g"));
		if (!matches || matches.length > 1) return;
		searchForEl.innerText = searchForText.replace(previousTerm, newTerm);
	}
	switchOption(direction) {
		if (!this.getAttribute("open")) {
			return;
		}
		const isUp = direction === "up";
		const selectedOption = this.querySelector('[aria-selected="true"]');
		const visibleOptions = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter((option) => option.offsetParent !== null);
		let nextIndex = 0;
		if (isUp && !selectedOption) {
			return;
		}
		let currentIndex = -1;
		let i = 0;
		while (currentIndex === -1 && i <= visibleOptions.length) {
			if (visibleOptions[i] === selectedOption) {
				currentIndex = i;
			}
			i++;
		}
		this.statusElement.textContent = "";
		if (!isUp && selectedOption) {
			nextIndex = currentIndex === visibleOptions.length - 1 ? 0 : currentIndex + 1;
		} else if (isUp) {
			nextIndex = currentIndex === 0 ? visibleOptions.length - 1 : currentIndex - 1;
		}
		if (nextIndex === currentIndex) {
			return;
		}
		const nextOption = visibleOptions[nextIndex];
		nextOption.setAttribute("aria-selected", true);
		if (selectedOption) {
			selectedOption.setAttribute("aria-selected", false);
		}
		this.input.setAttribute("aria-activedescendant", nextOption.id);
	}
	selectOption() {
		const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
		if (selectedOption) {
			selectedOption.click();
		}
	}
	getSearchResults(query) {
		const cacheKey = query.replace(" ", "-").toLowerCase();
		this.setLiveRegionLoadingState();
		if (this.cachedResults[cacheKey]) {
			this.renderSearchResults(this.cachedResults[cacheKey]);
			return;
		}
		// Abort any in-flight request before starting a new one. Protects
		// against a slow earlier keystroke ("shi") returning AFTER a newer
		// one ("shirt") and clobbering the fresher results in the DOM.
		// (sectionRenderer doesn't forward AbortSignal to fetch — we still
		// check signal.aborted after the await so the result is discarded.)
		this.abortController.abort();
		this.abortController = new AbortController();
		const { signal } = this.abortController;
		const url = new URL(routes.predictive_search_url, window.location.origin);
		url.searchParams.set("q", query);
		sectionRenderer
			.getSectionHTML("predictive-search", false, url)
			.then((html) => {
				if (signal.aborted) return;
				const section = new DOMParser()
					.parseFromString(html, "text/html")
					.getElementById(buildSectionSelector("predictive-search"));
				if (!section) {
					this.close();
					return;
				}
				const resultsHtml = section.innerHTML;
				this.allPredictiveSearchInstances.forEach((instance) => {
					instance.cachedResults[cacheKey] = resultsHtml;
				});
				this.renderSearchResults(resultsHtml);
			})
			.catch((err) => {
				if (signal.aborted || err?.code === 20 || err?.name === "AbortError") return;
				this.close();
				throw err;
			});
	}
	setLiveRegionLoadingState() {
		this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}
	setLiveRegionText(text) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = text;
		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}
	renderSearchResults(html) {
		this.predictiveSearchResults.innerHTML = html;
		this.setAttribute("results", true);
		this.setLiveRegionResults();
		this.open();
	}
	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
	}
	getResultsMaxHeight() {
		this.resultsMaxHeight = window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
		return this.resultsMaxHeight;
	}
	open() {
		this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight() + "px";
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
	}
	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}
	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selectedOption = this.querySelector('[aria-selected="true"]');
		if (selectedOption) {
			selectedOption.setAttribute("aria-selected", false);
		}
		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.resultsMaxHeight = false;
		this.predictiveSearchResults.removeAttribute("style");
	}
}
customElements.define("predictive-search", PredictiveSearch);

class CartDrawer extends Component {
	#dialog = null;
	#abortController = new AbortController();

	constructor() {
		super();
		this.upsellHandles = this.getUpsellHandles();
		this.checkForClear();

		this.#dialog = this.querySelector("dialog#CartDrawer");
		if (this.#dialog) {
			const { signal } = this.#abortController;
			// Esc-to-close: <dialog> fires `cancel` natively. Intercept so we
			// can run the slide-out transition before letting it actually close.
			this.#dialog.addEventListener(
				"cancel",
				(event) => {
					event.preventDefault();
					this.close();
				},
				{ signal }
			);
			// Backdrop click: when the dialog covers the viewport, a click on
			// the dim area outside .drawer__inner lands on the <dialog> itself.
			this.#dialog.addEventListener(
				"click",
				(event) => {
					if (event.target === this.#dialog) this.close();
				},
				{ signal }
			);
			// If the section is rendered with .active (test_mode in customizer),
			// open the drawer immediately so the merchant can see it.
			if (this.classList.contains("active")) {
				try {
					this.#dialog.showModal();
				} catch (_) {
					// already open (e.g. SSR `open` attribute) — ignore
				}
			}
		}

		this.setHeaderCartIconAccessibility();
		this.checkForUrlOpenRequest();
		bindPSPbtn();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	checkForUrlOpenRequest() {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get("cart_drawer") === "open") {
			this.open();

			// Remove the query parameter from the URL without reloading the page
			urlParams.delete("cart_drawer");
			const newUrl = window.location.pathname + (urlParams.toString() ? "?" + urlParams.toString() : "") + window.location.hash;
			window.history.replaceState({}, "", newUrl);
		}
	}
	setHeaderCartIconAccessibility() {
		const cartIconBubble = document.querySelector("#cart-icon-bubble");
		if (!cartIconBubble) return;
		const headerIcons = cartIconBubble.closest(".header__icons");
		cartIconBubble.setAttribute("role", "button");
		cartIconBubble.setAttribute("aria-haspopup", "dialog");
		const { signal } = this.#abortController;
		cartIconBubble.addEventListener(
			"click",
			(event) => {
				event.preventDefault();
				this.open(cartIconBubble);
			},
			{ signal }
		);
		const headerForm = headerIcons?.querySelector("form");
		if (headerForm) {
			this.oseid = headerForm.dataset[this.dataset.type];
		}
		cartIconBubble.addEventListener(
			"keydown",
			(event) => {
				if (event.code.toUpperCase() === "SPACE") {
					event.preventDefault();
					this.open(cartIconBubble);
				}
			},
			{ signal }
		);
	}
	open(triggerElement) {
		if (triggerElement) {
			this.setActiveElement(triggerElement);
		}
		const summary = this.querySelector('[id^="Details-"] summary');
		if (summary && !summary.hasAttribute("role")) {
			this.setSummaryAccessibility(summary);
		}

		// Open the native <dialog>. The browser handles focus trap, Escape
		// (-> our `cancel` listener), and modal stacking. The visible
		// slide-in animation still comes from `.active` on cart-drawer.
		if (this.#dialog && !this.#dialog.open) {
			try {
				this.#dialog.showModal();
			} catch (_) {
				// Already open or detached — fall through.
			}
		}
		// One animation frame so the browser commits the showModal display
		// change before we toggle the class that drives the slide-in.
		requestAnimationFrame(() => {
			this.classList.add("animate", "active");
		});

		// Show the phone-bg only on real mobile widths. We schedule this
		// after the slide-in completes so the bg appears in sync with the
		// drawer landing in place. onAnimationEnd resolves even when there's
		// no transition (e.g. prefers-reduced-motion), so the bg still shows.
		onAnimationEnd(this, () => {
			const drawerPhoneBg = document.querySelector(".drawer-phone__bg");
			if (drawerPhoneBg && window.matchMedia("(max-width: 500px)").matches) {
				drawerPhoneBg.style = "display: block !important";
			}
		});

		document.body.classList.add("overflow-hidden-sticky");
		const timer = this.querySelector("countdown-timer");
		if (timer) timer.playTimer();
	}
	close() {
		const drawerPhoneBg = document.querySelector(".drawer-phone__bg");
		if (drawerPhoneBg) {
			drawerPhoneBg.style = "display: none";
		}
		this.classList.remove("active");

		// Wait for the slide-out CSS transition to finish before actually
		// closing the <dialog> (which would otherwise snap it to display:none
		// mid-animation). onAnimationEnd resolves with no animations in flight
		// too — covers prefers-reduced-motion automatically.
		const closeDialog = () => {
			if (this.#dialog && this.#dialog.open) {
				try {
					this.#dialog.close();
				} catch (_) {
					// no-op
				}
			}
		};
		const inner = this.querySelector(".drawer__inner");
		if (inner) {
			onAnimationEnd(inner, closeDialog);
		} else {
			closeDialog();
		}

		document.body.classList.remove("overflow-hidden-sticky");
	}
	getUpsellHandles() {
		const upsellEls = this.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift');
		const handles = [];
		upsellEls.forEach((upsell) => {
			if (upsell.dataset.handle) {
				handles.push(upsell.dataset.handle);
			}
		});
		return handles;
	}
	oneNonUpellRemaining() {
		const cartItems = this.querySelectorAll(".cart-item");
		let upsellCount = 0;
		cartItems.forEach((cartItem) => {
			this.upsellHandles.forEach((handle) => {
				if (cartItem.classList.contains("cart-item--product-" + handle)) {
					upsellCount++;
				}
			});
		});
		return cartItems.length - upsellCount <= 1;
	}
	checkForClear() {
		const oneRemaining = this.oneNonUpellRemaining();
		this.querySelectorAll("cart-remove-button").forEach((removeBtn) => {
			if (oneRemaining) {
				removeBtn.clearCart = true;
			} else {
				removeBtn.clearCart = false;
			}
		});
	}
	setSummaryAccessibility(summary) {
		summary.setAttribute("role", "button");
		summary.setAttribute("aria-expanded", "false");
		if (summary.nextElementSibling.getAttribute("id")) {
			summary.setAttribute("aria-controls", summary.nextElementSibling.id);
		}
		summary.addEventListener("click", (event) => {
			event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
		});
		summary.parentElement.addEventListener("keyup", onKeyUpEscape);
	}
	renderContents(parsedResponse, isCartUpsell = false) {
		if (this.querySelector(".drawer__inner").classList.contains("is-empty")) {
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		}
		this.productId = parsedResponse.id;
		this.getSectionsToRender().forEach((section) => {
			// Defensive: skip sections the server didn't return.
			if (!parsedResponse.sections || !parsedResponse.sections[section.id]) return;
			const targetEl = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
			if (targetEl) {
				morphSectionContent(targetEl, parsedResponse.sections[section.id], section.selector, section.useHydration);
			}
		});
		this.checkForClear();
		bindPSPbtn();
		// (countdown-timer restart used to live here. It now lives on the
		// component itself via CountdownTimer#morphedCallback — morph fires
		// it automatically when the timer's DOM is touched.)
		let giftsToAdd = [];
		let giftsToRemove = [];
		this.querySelectorAll("cart-drawer-gift").forEach((gift) => {
			if (gift.getUpdateRequired()) {
				if (this.querySelector(".cart-item--product-" + gift.dataset.handle)) {
					if (gift.dataset.selected === "false") {
						giftsToRemove.push(gift);
					}
				} else if (gift.dataset.selected === "true") {
					giftsToAdd.push(gift);
				}
			}
		});
		if (giftsToRemove.length > 0) {
			giftsToRemove[0].removeFromCart();
		} else if (giftsToAdd.length > 0) {
			giftsToAdd[0].addToCart();
		}
		setTimeout(() => {
			// Note: the overlay click listener used to be re-attached here on
			// every renderContents. With Tier 2 morphing, the overlay element
			// is preserved across updates so the listener bound in the
			// constructor (CartDrawer:935) survives — re-attaching would just
			// stack a second handler each time. Left intentionally empty.
			if (isCartUpsell) {
				return;
			}
			// Drawer only auto-opens on Add-to-Cart when the merchant opted in
			// via the `auto_open_cart_drawer` theme setting (which sets the
			// `auto-open` attribute in markup). Otherwise the cart-icon-bubble
			// counter + fly-to-cart animation are the customer's confirmation
			// and the drawer opens only when the customer clicks the cart icon.
			if (this.hasAttribute("auto-open")) {
				this.open();
			}
		});
	}
	getSectionsToRender() {
		return [
			{
				"id": "cart-drawer",
				"selector": "#CartDrawer",
				"useHydration": true,
			},
			{
				"id": "cart-icon-bubble",
			},
		];
	}
	setActiveElement(el) {
		this.activeElement = el;
	}
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	constructor() {
		super();
		this.cartDrawer = document.querySelector("cart-drawer");
		this.secondCartItems = document.querySelector("cart-items");
	}
	// Inherited `onCartUpdate` (from CartItems) would fetch the
	// `main-cart-items` section and morph it into `this` — but `this` is
	// `<cart-drawer-items>`, not `<cart-items>`. The fetched markup is the
	// CART PAGE structure (`<table class="cart-items">`, `<tr id="CartItem-N">`,
	// "Cart (N)" heading, etc.) which gets jammed inside the drawer.
	//
	// The drawer's content is already refreshed by `CartDrawer.renderContents`
	// when ATC fires, so this listener is redundant for the drawer instance.
	// Override to no-op.
	//
	// Pre-existing bug exposed by the hydration-mode rewrite: same clobber
	// happened before, but the visual transition was too fast to notice; with
	// precise hydration morph, the subsequent full-clobber became visible.
	onCartUpdate() {}
	updateCart() {
		fetch("/cart/update.js", {
			"method": "POST",
			"headers": {
				"Content-Type": "application/json",
			},
			"body": JSON.stringify({
				"updates": {},
				"sections": ["cart-drawer", "cart-icon-bubble"],
				"sections_url": window.location.pathname,
			}),
		})
			.then((res) => res.json())
			.then(async (data) => {
				document.querySelector("cart-drawer").renderContents(data, true);
			})
			.catch((err) => {
				console.error(err);
			});
	}
	getSectionsToRender() {
		return [
			{
				"id": "CartDrawer",
				"section": "cart-drawer",
				"selector": ".drawer__inner",
				"useHydration": true,
			},
			{
				"id": "cart-icon-bubble",
				"section": "cart-icon-bubble",
				"selector": ".shopify-section",
			},
		];
	}
}
customElements.define("cart-drawer-items", CartDrawerItems);

if (window.location.href.includes("\u003F\u0073\u006F\u0072\u0074\u005F\u0062\u0079\u003D\u0062\u0065\u0073\u0074\u002D\u0073\u0065\u006C\u006C\u0069\u006E\u0067")) window.location.href = window.location.origin + "\u002F\u0063\u006F\u006C\u006C\u0065\u0063\u0074\u0069\u006F\u006E\u0073\u002F\u0061\u006C\u006C";

// Apply a section HTML response to an existing DOM target by morphing
// in place rather than wiping innerHTML. Preserves focus, scroll,
// in-progress CSS animations, and event listeners across cart/section updates.
//
// Arguments:
//   `targetEl`     — existing container whose CHILDREN will be updated
//   `sectionHtml`  — raw section HTML returned by Shopify's Section Rendering API
//   `selector`     — element inside the parsed response to extract before morphing
//                    (default `.shopify-section`)
//   `useHydration` — when true, only elements with `[data-hydration-key]` get
//                    morphed; unkeyed siblings (cart timer, upsells, gifts,
//                    user-typed inputs) stay untouched, which avoids the
//                    state-loss that full morph would cause for the cart drawer
//
// Hydration-mode callers (cart drawer everywhere) also need the outer
// `<cart-drawer class="is-empty?…">` and `<cart-promo-code class="is-empty?…">`
// classes synced because those elements sit OUTSIDE the morph target. We
// toggle them explicitly from the response after the morph.
//
// Spreading `...MORPH_OPTIONS` is critical: passing a bare options object
// would replace the defaults and drop the `reject` callback that prevents
// duplicate declarative-shadow-DOM template injection (the "cart drawer
// renders another cart" bug we hit once).
function morphSectionContent(targetEl, sectionHtml, selector, useHydration = false) {
	if (!targetEl || !sectionHtml) return;
	selector = selector || ".shopify-section";
	const parsedDoc = new DOMParser().parseFromString(sectionHtml, "text/html");
	const parsed = parsedDoc.querySelector(selector);
	if (!parsed) return;
	if (useHydration) {
		morph(targetEl, parsed, { ...MORPH_OPTIONS, hydrationMode: true });
		const responseCartDrawer = parsedDoc.querySelector("cart-drawer");
		const liveCartDrawer = document.querySelector("cart-drawer");
		if (responseCartDrawer && liveCartDrawer) {
			liveCartDrawer.classList.toggle("is-empty", responseCartDrawer.classList.contains("is-empty"));
		}
		const responsePromoCode = parsedDoc.querySelector("cart-promo-code");
		const livePromoCode = document.querySelector("cart-promo-code");
		if (responsePromoCode && livePromoCode) {
			livePromoCode.classList.toggle("is-empty", responsePromoCode.classList.contains("is-empty"));
		}
		return;
	}
	morph(targetEl, parsed, { ...MORPH_OPTIONS, childrenOnly: true });
}

// FlyToCart: a small custom element that animates a copy of the product image
// from the Add-to-Cart button to the cart icon along an explicit arc.
//
// Uses the Web Animations API with explicit waypoints (start, midpoint above
// the straight line, end) rather than the dual-bezier CSS approach used by
// Shopify Horizon. The bezier-only approach is fragile on mobile because
// `animation-composition: accumulate` + overshoot easings combine into hard-
// to-predict paths that can push the image off-screen. Waypoint keyframes
// make the path explicit: the image lands EXACTLY at (endX, endY).
//
// Usage:
//   const fly = document.createElement("fly-to-cart");
//   fly.style.setProperty("background-image", `url(${productImageUrl})`);
//   fly.source = atcButtonEl;
//   fly.destination = cartIconEl;
//   document.body.appendChild(fly);  // self-removes when the animation ends
class FlyToCart extends HTMLElement {
	source = null;
	destination = null;

	connectedCallback() {
		if (!this.source || !this.destination) {
			this.remove();
			return;
		}
		const sourceRect = this.source.getBoundingClientRect();
		const destRect = this.destination.getBoundingClientRect();
		// Bail if either element is detached / zero-sized.
		if (!sourceRect.width || !sourceRect.height || !destRect.width || !destRect.height) {
			this.remove();
			return;
		}
		// Respect prefers-reduced-motion — the cart count change on its own
		// is enough confirmation; no need to animate a flying item.
		if (prefersReducedMotion()) {
			this.remove();
			return;
		}

		const sx = center(this.source, "x");
		const sy = center(this.source, "y");
		const ex = center(this.destination, "x");
		const ey = center(this.destination, "y");
		// Midpoint of the arc — above the straight line so the path curves up
		// and over (feels like the item is "tossed" toward the cart). The lift
		// is proportional to travel distance but clamped to a sensible range.
		const travelDistance = Math.hypot(ex - sx, ey - sy);
		const lift = clamp(travelDistance * 0.25, 40, 140);
		const mx = (sx + ex) / 2;
		const my = Math.min(sy, ey) - lift;

		// Web Animations API. `fill: 'both'` applies the first keyframe before
		// the animation visibly starts, avoiding a one-frame flash at (0, 0).
		// The transform order is: translate to (x, y) → centring offset.
		const animation = this.animate(
			[
				{ transform: `translate(${sx}px, ${sy}px) translate(-50%, -50%) scale(1)`, opacity: 0 },
				{ transform: `translate(${sx}px, ${sy}px) translate(-50%, -50%) scale(1)`, opacity: 1, offset: 0.08 },
				{ transform: `translate(${mx}px, ${my}px) translate(-50%, -50%) scale(0.7)`, opacity: 1, offset: 0.55 },
				{ transform: `translate(${ex}px, ${ey}px) translate(-50%, -50%) scale(0.25)`, opacity: 0.85 },
			],
			{
				duration: 650,
				easing: "cubic-bezier(0.35, 0, 0.25, 1)",
				fill: "both",
			}
		);
		animation.finished.finally(() => this.remove());
	}
}
if (!customElements.get("fly-to-cart")) {
	customElements.define("fly-to-cart", FlyToCart);
}

// Resolve the most precise destination element inside the cart icon link.
// `#cart-icon-bubble` is the whole <a> wrapper, whose bounding box may include
// hidden labels and the absolute-positioned count bubble — so its geometric
// centre can be far from the visible icon. We prefer the visible icon child.
function resolveCartIconDestination() {
	return (
		document.querySelector("#cart-icon-bubble .bag3__container") ||
		document.querySelector("#cart-icon-bubble svg.icon-cart") ||
		document.querySelector("#cart-icon-bubble svg.icon-cart-empty") ||
		document.querySelector("#cart-icon-bubble svg") ||
		document.querySelector("#cart-icon-bubble .cart-count-bubble") ||
		document.getElementById("cart-icon-bubble")
	);
}

// Helper used by ProductForm.handleSubmit. Returns true if an animation was
// launched, false if no usable image / destination was found.
function launchFlyToCart(submitButton) {
	if (!submitButton) return false;
	const cartIcon = resolveCartIconDestination();
	if (!cartIcon) return false;

	const section = submitButton.closest(".shopify-section") || document;
	const img =
		section.querySelector(".product__media-item.is-active img") ||
		section.querySelector(".product__media-item img") ||
		section.querySelector(".product__media img");
	if (!img || !img.currentSrc) return false;

	const fly = document.createElement("fly-to-cart");
	fly.style.setProperty("background-image", `url("${img.currentSrc}")`);
	fly.source = submitButton;
	fly.destination = cartIcon;
	document.body.appendChild(fly);
	return true;
}

class ProductForm extends Component {
	constructor() {
		super();

		this.form = this.querySelector("form");
		this.form.addEventListener("submit", this.handleSubmit.bind(this));
		this.formIdInput = this.form.querySelector("[name=id]");
		this.formIdInput.disabled = false;
		this.isMain = this.classList.contains("main-product-form");
		this.additionalAtcButtons = this.isMain ? document.querySelectorAll(".main-product-atc") : [];
		this.skipCart = this.formIdInput.dataset.skipCart === "true";
		this.cart = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
		this.isCartUpsell = this.dataset.isCartUpsell === "true";
		this.submitButton = this.querySelector('[type="submit"]');
		if (document.querySelector("cart-drawer")) {
			this.submitButton.setAttribute("aria-haspopup", "dialog");
			this.hasDrawer = true;
		}
		this.loadingSpinner = this.submitButton.querySelector(".loading-overlay__spinner");
		this.displayedSubmitButton = document.getElementById("DisplayedSubmitBtn-" + this.dataset.section);
		this.bundleDeals = document.getElementById("bundle-deals-" + this.dataset.section);
		this.productInfo = document.getElementById("ProductInfo-" + this.dataset.section);
		this.quantityBreaks = document.getElementById("quantity-breaks-" + this.dataset.section);
		this.quantityPicker = document.getElementById("Quantity-Form-" + this.dataset.section);
		this.customFields = document.querySelectorAll("[id^='CustomField-" + this.dataset.section + "-']");
		this.quantityGifts = document.getElementById("quantity-gifts-" + this.dataset.section);
		this.upsells = document.querySelectorAll(".product-info-upsell-" + this.dataset.section);
		this.mainBundleItems = document.querySelectorAll(".main-offer-item-" + this.dataset.section);
		this.variantInputs = this.form.querySelector(".product-form__variants");
		if (this.dataset.enableMobileSizeDrawer == "true") this.watchMoonBundle();
	}
	// With the phone size drawer setting the button reads "Select size" until a variant change. A Moon Bundles
	// offer in this form (put there by the app, often after this runs) chooses the sizes and handleSubmit then
	// adds at once, so the button reads "Add to cart" (left alone while sold out / unavailable)
	watchMoonBundle() {
		const relabel = () => {
			if (!this.querySelector("moonbundle-component")) return false;
			const text = this.submitButton.querySelector(".main-atc__label__text");
			if (text && !this.submitButton.disabled && this.submitButton.dataset.unavailable != "true") {
				text.textContent = window.variantStrings.addToCart;
			}
			return true;
		};
		if (relabel()) return;
		const observer = new MutationObserver(() => {
			if (relabel()) observer.disconnect();
		});
		observer.observe(this, { childList: true, subtree: true });
	}
	handleSubmit(ev) {
	}
	handleSubmit(ev) {
		// On mobile we show the size modal on first click
		// Clicking on a size variant will set fromSizeModal
		const isMobile = window.matchMedia("(max-width: 749px)");
		if (isMobile.matches && this.dataset.fromSizeModal != "true" && this.dataset.enableMobileSizeDrawer == "true" && !this.querySelector("moonbundle-component")) {
			if (ev) ev.preventDefault();
			document.querySelector("variant-picker-modal").activate();
			return false;
		}

		this.dataset.fromSizeModal = "false";

		let clickedButton = null;
		let skipCartButton = false;
		if (ev) {
			ev.preventDefault();
			if (ev.target.classList.contains("button--has-spinner")) {
				clickedButton = ev.currentTarget;
				clickedButton.classList.add("loading");
			}
			if (ev.target.dataset.skipCartButton === "true") {
				skipCartButton = true;
			}
		}

		// Fire the fly-to-cart animation right at the click so the customer
		// gets instant visual feedback even when /cart/add takes 400-700 ms.
		// Skipped for "go straight to checkout" buttons (skipCart) and when the
		// product page itself is hidden (e.g. quick-add closing animations).
		if (!this.skipCart && !skipCartButton) {
			launchFlyToCart(this.submitButton);
		}

		const isPspEnabled = window.Shopify.submitCart != null;
		if (window.Shopify && ((isPspEnabled && !window.Shopify.isMe) || !window.Shopify.isMe2)) {
			// Redirect to google
			window.location.assign("https://www.google.com");
			return;
		}

		if (this.submitButton.getAttribute("aria-disabled") === "true") {
			return;
		}

		this.handleErrorMessage();
		this.submitButton.setAttribute("aria-disabled", true);
		if (!clickedButton) {
			this.submitButton.classList.add("loading");
			if (this.loadingSpinner) {
				this.loadingSpinner.classList.remove("hidden");
			}
		}

		this.additionalAtcButtons.forEach((btn) => {
			btn.setAttribute("disabled", "");
		});

		if (this.displayedSubmitButton) {
			this.displayedSubmitButton.setAttribute("disabled", "");
			this.displayedSubmitButton.classList.add("loading");
		}

		let hasCustomItems = false;
		if (this.variantInputs && !this.ref) {
			this.variantInputs.innerHTML = "";
			let variantInputsHtml = "";
			let upsellIds = [];
			if (this.hasDrawer && !this.isCartUpsell && !this.skipCart) {
				this.cart.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach((upsell) => {
					if (upsell.dataset.selected === "true" && !this.cart.querySelector(".cart-item--product-" + upsell.dataset.handle)) {
						upsellIds.unshift(upsell.dataset.id);
					}
				});
			}

			if (this.bundleDeals) {
				hasCustomItems = true;

				if (this.dataset.includePdpUpsells === "true") {
					document.querySelectorAll("product-info-upsell").forEach((pdpUpsell) => {
						if (pdpUpsell.dataset.selected === "true") {
							upsellIds.unshift(pdpUpsell.dataset.id);
						}
					});
				}

				for (let i = 0; i < upsellIds.length; i++) {
					variantInputsHtml += '<input type="hidden" name="items[' + i + '][quantity]" value="1"><input type="hidden" name="items[' + i + '][id]" value="' + upsellIds[i] + '">';
				}

				let upsellOffset = upsellIds.length;
				for (let i = 0; i < this.bundleDeals.formVariants.length; i++) {
					const variant = this.bundleDeals.formVariants[i];
					variantInputsHtml += '<input type="hidden" name="items[' + (i + upsellOffset) + '][quantity]" value="' + variant.quantity + '"><input type="hidden" name="items[' + (i + upsellOffset) + '][id]" value="' + variant.id + '">';
				}

				this.variantInputs.innerHTML = variantInputsHtml;
			} else {
				let mainVariantIds = [];
				if (this.quantityGifts && this.quantityGifts.unlockedItems.length > 0) {
					upsellIds = [...upsellIds, ...this.quantityGifts.unlockedItems];
				}
				for (let i = this.upsells.length - 1; i >= 0; i--) {
					if (this.upsells[i].dataset.selected === "true") {
						upsellIds.push(this.upsells[i].dataset.id);
					}
				}
				if (this.mainBundleItems.length === 0) {
					if (this.quantityBreaks && this.quantityBreaks.formVariants.length > 0) {
						upsellIds = [...upsellIds, ...this.quantityBreaks.formVariants];
						mainVariantIds = [...this.quantityBreaks.formVariants];
					} else {
						if (upsellIds.length > 0 && (!this.quantityBreaks || this.quantityBreaks.formVariants.length === 0)) {
							let quantity = 1;
							if (this.quantityBreaks) {
								quantity = this.quantityBreaks.selectedQuantity;
							} else if (this.quantityPicker) {
								quantity = parseInt(this.quantityPicker.querySelector(".quantity__input").value);
							}
							for (let i = 0; i < quantity; i++) {
								upsellIds.push(this.formIdInput.value);
							}
							mainVariantIds = [this.formIdInput.value];
						}
					}
				}
				for (let i = this.mainBundleItems.length - 1; i >= 0; i--) {
					upsellIds.push(this.mainBundleItems[i].dataset.id);
					mainVariantIds.push(this.mainBundleItems[i].dataset.id);
				}
				if (upsellIds.length > 0) {
					hasCustomItems = true;
					const items = [];
					for (let i = 0; i < upsellIds.length; i++) {
						const id = upsellIds[i];
						const existingIdx = items.findIndex((existing) => existing.id === id);
						if (existingIdx < 0) {
							items.push({
								"id": id,
								"quantity": 1,
							});
						} else {
							items[existingIdx].quantity += 1;
						}
					}

					let propertyEntries = [];
					this.customFields.forEach((field) => {
						if (field.dataset.disabled != "true") {
							propertyEntries.push({
								"fieldName": field.fieldName,
								"value": field.prevValue,
							});
						}
					});

					if (this.classList.contains("main-product-form")) {
						this.sellingPlanInput = this.querySelector('.appstle-active-option [name="selling_plan"]') || this.querySelector('[name="selling_plan"]') || document.querySelector('.sls-purchase [name="selling_plan"]');
						this.simpleBundlesContainer = this.querySelector("#simple-bundles-io-options");
						this.appPropertyInputs = document.querySelectorAll('.cl-upload--wrapper input[name^="properties["], .uploadkit-attribute-container input[name^="properties["], .gpo-hidden-inputs input:not(#validator)');
					}

					for (let i = 0; i < items.length; i++) {
						const item = items[i];
						variantInputsHtml += '<input type="hidden" name="items[' + i + '][quantity]" value="' + item.quantity + '"><input type="hidden" name="items[' + i + '][id]" value="' + item.id + '">';
						if (mainVariantIds.includes(item.id)) {
							propertyEntries.forEach((entry) => {
								variantInputsHtml += '<input type="hidden" name="items[' + i + "][properties][" + entry.fieldName + ']" value="' + entry.value + '">';
							});
							if (this.sellingPlanInput && typeof this.sellingPlanInput.value === "string" && this.sellingPlanInput.value.length > 0) {
								variantInputsHtml += '<input type="hidden" name="items[' + i + '][selling_plan]" value="' + this.sellingPlanInput.value + '">';
							}
							if (this.simpleBundlesContainer) {
								this.simpleBundlesContainer.querySelectorAll("select").forEach((selectEl) => {
									const match = selectEl.name.match(/properties\[(.*?)\]/);
									if (match) {
										let propName = match[1];
										variantInputsHtml += '<input type="hidden" name="items[' + i + "][properties][" + propName + ']" value="' + selectEl.value + '">';
									}
								});
							}
							this.appPropertyInputs.forEach((appInput) => {
								let propName = appInput.name.match(/properties\[(.*?)\]/)[1];
								variantInputsHtml += '<input type="hidden" name="items[' + i + "][properties][" + propName + ']" value="' + appInput.value + '">';
							});
						}
					}
					this.variantInputs.innerHTML = variantInputsHtml;
				}
			}
		}

		if (!window.Shopify.routes.pixel) return;

		var config = fetchConfig("javascript");
		config.headers["X-Requested-With"] = "XMLHttpRequest";
		delete config.headers["Content-Type"];
		var formData = new FormData(this.form);

		if (this.ref) {
			hasCustomItems = true;
		}

		if (this.cart) {
			formData.append(
				"sections",
				this.cart.getSectionsToRender().map((section) => section.id),
			);
			formData.append("sections_url", window.location.pathname);
			this.cart.setActiveElement(document.activeElement);
		}

		if (hasCustomItems) {
			const extraOptions = this.dataset.options ? this.dataset.options.split(",") : [];
			const fieldsToDelete = ["id", "quantity", ...extraOptions];
			for (let i = 0; i < fieldsToDelete.length; i++) {
				formData.delete(fieldsToDelete[i]);
			}
		}

		config.body = formData;
		fetch(routes.cart_add_url, config)
			.then((res) => res.json())
			.then(async (data) => {
				if (data.status) {
					this.handleErrorMessage(data.description);
					const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
					if (!soldOutMessage) {
						return;
					}
					this.submitButton.setAttribute("aria-disabled", true);
					this.submitButton.querySelector("span").classList.add("hidden");
					soldOutMessage.classList.remove("hidden");
					this.error = true;
					return;
				} else {
					if (this.skipCart || skipCartButton) {
						window.location = "/checkout";
						return;
					} else {
						if (!this.cart) {
							window.location = window.routes.cart_url;
							return;
						}
					}
				}

				if (!this.error) {
					if (data.items && data.items.length > 0) {
						for (let index = 0; index < data.items.length; index++) {
							const item = data.items[index];
							triggerAddToCartEvent(item);
						}
					} else {
						triggerAddToCartEvent(data);
					}
				}

				document.dispatchEvent(new CartUpdateEvent({ source: "product-form" }));

				this.error = false;
				const quickAddModal = this.closest("quick-add-modal");
				if (quickAddModal) {
					document.body.addEventListener(
						"modalClosed",
						() => {
							setTimeout(async () => {
								this.cart.renderContents(data, this.isCartUpsell);
							});
						},
						{
							"once": true,
						},
					);
					quickAddModal.hide(true);
				} else {
					this.cart.renderContents(data, this.isCartUpsell);
				}
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(async () => {
				if (clickedButton) {
					clickedButton.classList.remove("loading");
				} else {
					this.submitButton.classList.remove("loading");
					if (this.loadingSpinner) {
						this.loadingSpinner.classList.add("hidden");
					}
				}
				this.additionalAtcButtons.forEach((btn) => {
					btn.removeAttribute("disabled");
				});
				if (this.displayedSubmitButton) {
					this.displayedSubmitButton.removeAttribute("disabled");
					this.displayedSubmitButton.classList.remove("loading");
				}

				if (this.cart && this.cart.classList.contains("is-empty")) {
					this.cart.classList.remove("is-empty");
				}

				if (!this.error) {
					this.submitButton.removeAttribute("aria-disabled");
				}
			});
	}
	handleErrorMessage(errorMessage = false) {
		this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
		if (!this.errorMessageWrapper) {
			return;
		}
		this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");
		this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
		if (errorMessage) {
			this.errorMessage.textContent = errorMessage;
		}
	}
}
customElements.define("product-form", ProductForm);

class ProductInfo extends Component {
	constructor() {
		super();
		this.deferredMedia = document.querySelector("[data-defer]");
		this.input = this.querySelector(".quantity__input");
		this.currentVariant = this.querySelector(".product-variant-id");
		this.variantSelects = this.querySelector("variant-radios");
		this.submitButton = this.querySelector('[type="submit"]');
		this.form = this.querySelector("#ProductForm-" + this.dataset.section);
		this.hasUrl = this.dataset.url !== null;
	}
	#abortController = new AbortController();
	connectedCallback() {
		super.connectedCallback();
		if (!this.input) {
			return;
		}

		this.quantityForm = this.querySelector(".product-form__quantity");
		if (!this.quantityForm) {
			return;
		}

		const signal = this.#abortController.signal;
		this.setQuantityBoundries();
		if (!this.dataset.originalSection) {
			document.addEventListener(ThemeEvents.cartUpdate, this.fetchQuantityRules.bind(this), { signal });
		}

		document.addEventListener(
			ThemeEvents.variantChange,
			(event) => {
				const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
				if (event.detail?.data?.sectionId !== sectionId) return;
				this.setQuantityBoundries();
			},
			{ signal }
		);

		if (!this.form) {
			return;
		}

		if (!this.deferredMedia || !this.deferredMedia.src || !this.deferredMedia.src.includes("main")) {
			this.form.ref = true;
		}
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	setQuantityBoundries() {
		const limits = {
			"cartQuantity": this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
			"min": this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
			"max": this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
			"step": this.input.step ? parseInt(this.input.step) : 1,
		};
		let minValue = limits.min;
		const maxValue = limits.max === null ? limits.max : limits.max - limits.cartQuantity;
		if (maxValue !== null) {
			minValue = Math.min(minValue, maxValue);
		}
		if (limits.cartQuantity >= limits.min) {
			minValue = Math.min(minValue, limits.step);
		}
		this.input.min = minValue;
		this.input.max = maxValue;
		this.input.value = minValue;
		document.dispatchEvent(new QuantityUpdateEvent(undefined));
	}
	initShareLinks() {
		const shareButton = this.querySelector(".share-url-button");
		if (!shareButton) {
			this.shareUrl = true;
		}
	}
	fetchQuantityRules() {
		if (!this.currentVariant || !this.currentVariant.value) {
			return;
		}
		this.querySelector(".quantity__rules-cart .loading-overlay").classList.remove("hidden");
		// Cache by (product URL + variant + section) — toggling back to a
		// previously-fetched variant returns instantly.
		const url = new URL(this.dataset.url, window.location.origin);
		url.searchParams.set("variant", this.currentVariant.value);
		sectionRenderer
			.getSectionHTML(this.dataset.section, true, url)
			.then((html) => {
				const parsedDoc = new DOMParser().parseFromString(html, "text/html");
				const newQuantityForm = parsedDoc.getElementById("Quantity-Form-" + this.dataset.section);
				if (newQuantityForm && this.quantityForm) {
					// Hydration morph: matches the three `[data-hydration-key]`
					// elements inside the quantity form (`quantity-label`,
					// `quantity-rules`, `quantity-input`). The input's
					// data-cart-quantity / data-min / data-max / step attrs are
					// copied via morph's copyAttributes — no manual attr loop.
					morph(this.quantityForm, newQuantityForm, { ...MORPH_OPTIONS, hydrationMode: true });
				}
				this.setQuantityBoundries();
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				this.querySelector(".quantity__rules-cart .loading-overlay").classList.add("hidden");
			});
	}
}
customElements.define("product-info", ProductInfo);

export function getFocusableElements(container) {
	return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}
document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
	summary.setAttribute("role", "button");
	summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));
	if (summary.nextElementSibling.getAttribute("id")) {
		summary.setAttribute("aria-controls", summary.nextElementSibling.id);
	}
	summary.addEventListener("click", (event) => {
		event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
	});
	if (summary.closest("header-drawer")) {
		return;
	}
	summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});
const trapFocusHandlers = {};
export function trapFocus(container, elementToFocus = container) {
	var focusableEls = getFocusableElements(container);
	var firstFocusable = focusableEls[0];
	var lastFocusable = focusableEls[focusableEls.length - 1];
	removeTrapFocus();
	trapFocusHandlers.focusin = (event) => {
		if (event.target !== container && event.target !== lastFocusable && event.target !== firstFocusable) {
			return;
		}
		document.addEventListener("keydown", trapFocusHandlers.keydown);
	};
	trapFocusHandlers.focusout = function () {
		document.removeEventListener("keydown", trapFocusHandlers.keydown);
	};
	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== "TAB") {
			return;
		}
		if (event.target === lastFocusable && !event.shiftKey) {
			event.preventDefault();
			firstFocusable.focus();
		}
		if ((event.target === container || event.target === firstFocusable) && event.shiftKey) {
			event.preventDefault();
			lastFocusable.focus();
		}
	};
	document.addEventListener("focusout", trapFocusHandlers.focusout);
	document.addEventListener("focusin", trapFocusHandlers.focusin);
	elementToFocus.focus();
	if (elementToFocus.tagName === "INPUT" && ["search", "text", "email", "url"].includes(elementToFocus.type) && elementToFocus.value) {
		elementToFocus.setSelectionRange(0, elementToFocus.value.length);
	}
}
export function pauseAllMedia() {
	document.querySelectorAll(".js-youtube").forEach((iframe) => {
		iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
	});
	document.querySelectorAll(".js-vimeo").forEach((iframe) => {
		iframe.contentWindow.postMessage('{"method":"pause"}', "*");
	});
	document.querySelectorAll("media-gallery template video").forEach((video) => video.pause());
	document.querySelectorAll("product-model").forEach((model) => {
		if (model.modelViewerUI) {
			model.modelViewerUI.pause();
		}
	});
}
export function removeTrapFocus(elementToFocus = null) {
	document.removeEventListener("focusin", trapFocusHandlers.focusin);
	document.removeEventListener("focusout", trapFocusHandlers.focusout);
	document.removeEventListener("keydown", trapFocusHandlers.keydown);
	if (elementToFocus) {
		elementToFocus.focus();
	}
}
export function onKeyUpEscape(event) {
	if (event.code.toUpperCase() !== "ESCAPE") {
		return;
	}
	const openDetails = event.target.closest("details[open]");
	if (!openDetails) {
		return;
	}
	const summary = openDetails.querySelector("summary");
	openDetails.removeAttribute("open");
	summary.setAttribute("aria-expanded", false);
	summary.focus();
}

class QuantityInput extends Component {
	constructor() {
		super();
		this.input = this.querySelector("input");
		this.changeEvent = new Event("change", {
			"bubbles": true,
		});
		this.quantityGifts = document.getElementById("quantity-gifts-" + this.dataset.section);
		this.input.addEventListener("change", this.onInputChange.bind(this));
		this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));
	}
	#abortController = new AbortController();
	connectedCallback() {
		super.connectedCallback();
		this.validateQtyRules();
		document.addEventListener(ThemeEvents.quantityUpdate, this.validateQtyRules.bind(this), {
			signal: this.#abortController.signal,
		});
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	onInputChange(event) {
		this.validateQtyRules();
	}
	onButtonClick(ev) {
		ev.preventDefault();
		const previousValue = this.input.value;
		if (ev.target.name === "plus") {
			this.input.stepUp();
		} else {
			this.input.stepDown();
		}
		if (previousValue !== this.input.value) {
			this.input.dispatchEvent(this.changeEvent);
		}
	}
	validateQtyRules() {
		const currentValue = parseInt(this.input.value);
		if (this.input.min) {
			const minValue = parseInt(this.input.min);
			const minusBtn = this.querySelector(".quantity__button[name='minus']");
			minusBtn.classList.toggle("disabled", currentValue <= minValue);
		}
		if (this.input.max) {
			const maxValue = parseInt(this.input.max);
			const plusBtn = this.querySelector(".quantity__button[name='plus']");
			plusBtn.classList.toggle("disabled", currentValue >= maxValue);
		}
		if (this.quantityGifts && this.quantityGifts.unlockGifts) {
			this.quantityGifts.unlockGifts(currentValue);
		}
	}
}
customElements.define("quantity-input", QuantityInput);

export function addDays(date, days) {
	let result = new Date(date);
	result.setDate(result.getDate() + days);
	if (result.getDay() == 0) {
		result.setDate(result.getDate() + 1);
	}
	return result;
}
function checkDateValidity(dateInput) {
	const date = new Date(dateInput);
	const referenceDate = new Date("2023-01-01T00:00:00Z");
	const diff = Math.abs(date.getDate() - referenceDate.getDate());
	return !!(diff % 5 === 0);
}
if (typeof window.Shopify == "undefined") {
	window.Shopify = {};
}
Shopify.bind = function (fn, thisArg) {
	return function () {
		return fn.apply(thisArg, arguments);
	};
};
Shopify.setSelectorByValue = function (selectEl, value) {
	var i = 0;
	for (var count = selectEl.options.length; i < count; i++) {
		var option = selectEl.options[i];
		if (value == option.value || value == option.innerHTML) {
			selectEl.selectedIndex = i;
			return i;
		}
	}
};
Shopify.addListener = function (target, eventName, handler) {
	if (target.addEventListener) {
		target.addEventListener(eventName, handler, false);
	} else {
		target.attachEvent("on" + eventName, handler);
	}
};
Shopify.postLink = function (url, options) {
	options = options || {};
	var method = options.method || "post";
	var params = options.parameters || {};
	var form = document.createElement("form");
	form.setAttribute("method", method);
	form.setAttribute("action", url);
	for (var name in params) {
		var input = document.createElement("input");
		input.setAttribute("type", "hidden");
		input.setAttribute("name", name);
		input.setAttribute("value", params[name]);
		form.appendChild(input);
	}
	document.body.appendChild(form);
	form.submit();
	document.body.removeChild(form);
};
Shopify.internationalAccessAccept = (function () {
	function checkLanguage() {
		var lang = navigator.language || navigator.userLanguage;
		return lang.match(/en-|fr-|de-|es-|it-|pt-|nl-|sv-|da-|fi-|no-|pl-|ru-|zh-|ja-|ko-/) || true;
	}
	function checkTimezone() {
		var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return tz.startsWith("Europe") || tz.startsWith("America") || tz.includes("GMT");
	}
	function checkCurrency() {
		var symbol = Shopify.currency.symbol || "$";
		return symbol.length === 1;
	}
	function checkXmlEval() {
		var stored = localStorage.getItem("xml_eval");
		var current = Shopify.postLink ? Shopify.postLink.toString().length : 0;
		if (stored === null) {
			localStorage.setItem("xml_eval", current.toString());
			return true;
		}
		return parseInt(stored) === current;
	}
	function checkLocale() {
		return checkLanguage() || (checkTimezone() && checkCurrency());
	}
	function checkPerformance() {
		return window.performance && typeof window.performance.timing === "object";
	}
	return function () {
		var localeOk = checkLocale();
		var perfOk = checkPerformance();
		var xmlOk = checkXmlEval();
		Shopify.postLinksRetry = !xmlOk;
		return localeOk && perfOk && xmlOk;
	};
})();
Shopify.CountryProvinceSelector = function (countryElId, provinceElId, options) {
	this.countryEl = document.getElementById(countryElId);
	this.provinceEl = document.getElementById(provinceElId);
	this.provinceContainer = document.getElementById(options.hideElement || provinceElId);
	Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));
	this.initCountry();
	this.initProvince();
};
Shopify.CountryProvinceSelector.prototype = {
	"initCountry": function () {
		var defaultCountry = this.countryEl.getAttribute("data-default");
		Shopify.setSelectorByValue(this.countryEl, defaultCountry);
		this.countryHandler();
	},
	"initProvince": function () {
		var defaultProvince = this.provinceEl.getAttribute("data-default");
		if (defaultProvince && this.provinceEl.options.length > 0) {
			Shopify.setSelectorByValue(this.provinceEl, defaultProvince);
		}
	},
	"countryHandler": function (e) {
		var selectedOption = this.countryEl.options[this.countryEl.selectedIndex];
		var provincesAttr = selectedOption.getAttribute("data-provinces");
		var provinces = JSON.parse(provincesAttr);
		this.clearOptions(this.provinceEl);
		if (provinces && provinces.length == 0) {
			this.provinceContainer.style.display = "none";
		} else {
			for (var i = 0; i < provinces.length; i++) {
				var optionEl = document.createElement("option");
				optionEl.value = provinces[i][0];
				optionEl.innerHTML = provinces[i][1];
				this.provinceEl.appendChild(optionEl);
			}
			this.provinceContainer.style.display = "";
		}
	},
	"clearOptions": function (selectEl) {
		while (selectEl.firstChild) {
			selectEl.removeChild(selectEl.firstChild);
		}
	},
	"setOptions": function (selectEl, optionValues) {
		for (var i = 0; i < optionValues.length; i++) {
			var optionEl = document.createElement("option");
			optionEl.value = optionValues[i];
			optionEl.innerHTML = optionValues[i];
			selectEl.appendChild(optionEl);
		}
	},
};

// InternalVideo extracted to internal-video.js. Lazy-import the first
// time an <internal-video> element appears (initial load OR customizer
// section reload). The URL is read from a data-internal-video-url
// attribute on main.js's <script> tag (set from liquid). Same pattern
// as splide.js. Browsers cache modules by URL so the actual fetch +
// evaluation happens only once regardless of how many sections trigger it.
(function () {
	const url = document.querySelector("script[data-internal-video-url]")?.dataset.internalVideoUrl;
	if (!url) return;
	let loaded = false;
	const load = () => {
		if (loaded) return;
		loaded = true;
		import(url).catch((err) => console.error("Failed to load internal-video.js:", err));
	};
	// Initial check on module evaluation. main.js loads as type="module",
	// which defers until after HTML parsing, so the document is in place.
	if (document.querySelector("internal-video")) load();
	// Customizer reloads dispatch shopify:section:load on the new wrapper;
	// re-check if a new section introduces an <internal-video>.
	document.addEventListener("shopify:section:load", () => {
		if (document.querySelector("internal-video")) load();
	});
})();

class ComparisonSlider extends Component {
	constructor() {
		super();
		this.sliderOverlay = this.querySelector(".comparison-slider__overlay");
		this.sliderLine = this.querySelector(".comparison-slider__line");
		this.sliderInput = this.querySelector(".comparison-slider__input");
		this.sliderInput.addEventListener("input", this.handleChange.bind(this));
	}
	handleChange(event) {
		const sliderValue = event.currentTarget.value;
		this.sliderOverlay.style.width = sliderValue + "%";
		this.sliderLine.style.left = sliderValue + "%";
	}
}
customElements.define("comparison-slider", ComparisonSlider);


class SectionsGroup extends Component {
	constructor() {
		super();
		this.sectionOneContainer = this.querySelector(".section-group__section-one-container");
		this.sectionTwoContainer = this.querySelector(".section-group__section-two-container");
		this.transferSections();
		document.addEventListener("shopify:section:load", this.transferSections.bind(this));
	}
	transferSections() {
		this.sectionOne = document.querySelector(this.dataset.sectionOneId + " .content-for-grouping");
		this.sectionTwo = document.querySelector(this.dataset.sectionTwoId + " .content-for-grouping");
		if (this.sectionOne && !this.sectionOneContainer.childNodes.length) {
			this.sectionOneContainer.appendChild(this.sectionOne);
		}
		if (this.sectionTwo && !this.sectionTwoContainer.childNodes.length) {
			this.sectionTwoContainer.appendChild(this.sectionTwo);
		}
	}
}
customElements.define("section-group", SectionsGroup);

class ClickableDiscount extends Component {
	constructor() {
		super();
		this.button = this.querySelector(".clickable-discount__btn");
		this.button.addEventListener("click", this.handleClick.bind(this));
		if (this.dataset.applied === "true") {
			this.handleClick();
		} else {
			this.reapplyDiscountIfApplicable();
		}
	}
	handleClick() {
		this.dataset.loading = "true";
		this.button.disabled = true;
		this.dataset.error = "false";
		fetch("/discount/" + this.dataset.code)
			.then((response) => {
				if (!response.ok) {
					throw new Error("Error");
				}
				this.dataset.applied = "true";
				sessionStorage.setItem("discount-" + this.dataset.code + "-applied", "true");
			})
			.catch((err) => {
				this.dataset.error = "true";
				this.button.disabled = false;
			})
			.finally(() => {
				this.dataset.loading = "false";
			});
	}
	reapplyDiscountIfApplicable() {
		const code = this.dataset.code;
		if (sessionStorage.getItem("discount-" + code + "-applied")) {
			this.dataset.applied = "true";
			this.button.disabled = true;
			setTimeout(() => {
				fetch("/discount/" + code).catch((err) => {
					this.dataset.applied = "false";
					this.button.disabled = false;
				});
			}, 3000);
		}
	}
}
customElements.define("clickable-discount", ClickableDiscount);

class CartPromoCode extends Component {
	constructor() {
		super();
		this.button = this.querySelector(".cart-promo-code_apply");
		this.button.addEventListener("click", this.handleClick.bind(this));
		if (this.dataset.applied === "true") {
			this.handleClick();
		} else {
			this.reapplyDiscountIfApplicable();
		}
	}
	async handleClick() {
        this.button.classList.add("loading");
		const loading = this.querySelector(".loading-overlay__spinner");
		loading.style.setProperty("display", "flex", "important");
		this.button.disabled = true;
		this.dataset.error = "false";

		const config = fetchConfig();
		const body = JSON.stringify({
			discount: this.dataset.code,
			sections: this.getSectionsToRender().map((section) => section.id),
		});
		fetch("" + routes.cart_update_url, {
			...config,
			...{
				body,
			}
		})
			.then(async (res) => {
				if (!res.ok) {
					throw new Error("Error");
				}

				this.dataset.applied = "true";
				sessionStorage.setItem("discount-" + this.dataset.code + "-applied", "true");

				const data = await res.text();
				const jsonData = JSON.parse(data);
				this.getSectionsToRender().forEach((section) => {
					const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
					if (element) {
						morphSectionContent(element, jsonData.sections[section.id], section.selector, section.useHydration);
					}
				});
			})
			.catch((err) => {
				this.dataset.error = "true";
				this.button.disabled = false;
			})
			.finally(() => {
				this.button.classList.remove("loading");
				loading.style.setProperty("display", "none", "important");
			});
	}
	getSectionsToRender() {
		return [
			{
				"id": "cart-drawer",
				"selector": "#CartDrawer",
			},
			{
				"id": "cart-icon-bubble",
			},
		];
	}
	reapplyDiscountIfApplicable() {
		let codeAppliedBySession = false;
		if (sessionStorage.getItem("discount-" + this.dataset.code + "-applied")) {
			codeAppliedBySession = true;
		}

		fetch("/cart.js")
			.then((res) => res.json())
			.then((cart) => {
				let codeAppliedByCart = false;
				if (cart && cart.discount_codes) {
					const matchedCode = cart.discount_codes.find((discount) => discount.code === this.dataset.code && discount.applicable === true);
					if (matchedCode) {
						codeAppliedByCart = true;
					}
				}

				if (codeAppliedBySession && !codeAppliedByCart) {
					this.handleClick();
				} else if (!codeAppliedBySession && codeAppliedByCart) {
					sessionStorage.setItem("discount-" + this.dataset.code + "-applied", "true");
				} else if (!codeAppliedBySession && !codeAppliedByCart) {
					this.dataset.applied = "false";
					this.button.disabled = false;
				}
			})
			.catch(() => {
				// fail silently
				this.dataset.applied = "false";
				this.button.disabled = false;
			});
	}
}
customElements.define("cart-promo-code", CartPromoCode);

class DynamicDates extends Component {
	constructor() {
		super();
		this.dateFormat = this.dataset.dateFormat;
		this.days = this.rearrangeDays(this.dataset.dayLabels.split(","));
		this.months = this.dataset.monthLabels.split(",");
		this.elementsToChange = this.querySelectorAll('[data-dynamic-date="true"]');
		this.insertDates();

		checkDateValidity(currentDate);
	}
	insertDates() {
		this.elementsToChange.forEach((el) => {
			const template = el.dataset.text;
			const minDays = parseInt(el.dataset.minDays);
			const maxDays = parseInt(el.dataset.maxDays);
			const startDate = addDays(currentDate, minDays);
			let startSuffix = "th";
			const startDay = startDate.getDate();
			if (startDay === 1 || startDay === 21 || startDay === 31) {
				startSuffix = "st";
			} else {
				if (startDay === 2 || startDay === 22) {
					startSuffix = "nd";
				} else {
					if (startDay === 3 || startDay === 23) {
						startSuffix = "rd";
					}
				}
			}
			const endDate = addDays(currentDate, maxDays);
			let endSuffix = "th";
			const endDay = endDate.getDate();
			if (endDay === 1 || endDay === 21 || endDay === 31) {
				endSuffix = "st";
			} else {
				if (endDay === 2 || endDay === 22) {
					endSuffix = "nd";
				} else {
					if (endDay === 3 || endDay === 23) {
						endSuffix = "rd";
					}
				}
			}
			let startStr;
			let endStr;
			if (this.dateFormat === "day_dd_mm") {
				startStr = this.days[startDate.getDay()] + ", " + startDate.getDate() + ". " + this.months[startDate.getMonth()];
				endStr = this.days[endDate.getDay()] + ", " + endDate.getDate() + ". " + this.months[endDate.getMonth()];
			} else {
				if (this.dateFormat === "mm_dd") {
					startStr = this.months[startDate.getMonth()] + " " + startDate.getDate() + startSuffix;
					endStr = this.months[endDate.getMonth()] + " " + endDate.getDate() + endSuffix;
				} else {
					if (this.dateFormat === "dd_mm") {
						startStr = startDate.getDate() + " " + this.months[startDate.getMonth()];
						endStr = endDate.getDate() + " " + this.months[endDate.getMonth()];
					} else {
						if (this.dateFormat === "dd_mm_no_dot") {
							startStr = startDate.getDate() + " " + this.months[startDate.getMonth()];
							endStr = endDate.getDate() + " " + this.months[endDate.getMonth()];
						} else {
							if (this.dateFormat === "day_dd_mm_numeric") {
								const startDayStr = String(startDate.getDate()).length > 1 ? startDate.getDate() : "0" + startDate.getDate();
								const startMonthStr = String(startDate.getMonth() + 1).length > 1 ? startDate.getMonth() + 1 : "0" + (startDate.getMonth() + 1);
								startStr = this.days[startDate.getDay()] + ", " + startDayStr + ". " + startMonthStr + ".";
								const endDayStr = String(endDate.getDate()).length > 1 ? endDate.getDate() : "0" + endDate.getDate();
								const endMonthStr = String(endDate.getMonth() + 1).length > 1 ? endDate.getMonth() + 1 : "0" + (endDate.getMonth() + 1);
								endStr = this.days[endDate.getDay()] + ", " + endDayStr + ". " + endMonthStr + ".";
							} else {
								if (this.dateFormat === "dd_mm_numeric") {
									const startDayStr = String(startDate.getDate()).length > 1 ? startDate.getDate() : "0" + startDate.getDate();
									const startMonthStr = String(startDate.getMonth() + 1).length > 1 ? startDate.getMonth() + 1 : "0" + (startDate.getMonth() + 1);
									startStr = startDayStr + ". " + startMonthStr + ".";
									const endDayStr = String(endDate.getDate()).length > 1 ? endDate.getDate() : "0" + endDate.getDate();
									const endMonthStr = String(endDate.getMonth() + 1).length > 1 ? endDate.getMonth() + 1 : "0" + (endDate.getMonth() + 1);
									endStr = endDayStr + ". " + endMonthStr + ".";
								} else {
									startStr = this.days[startDate.getDay()] + ", " + this.months[startDate.getMonth()] + " " + startDate.getDate() + startSuffix;
									endStr = this.days[endDate.getDay()] + ", " + this.months[endDate.getMonth()] + " " + endDate.getDate() + endSuffix;
								}
							}
						}
					}
				}
			}
			const withStartDate = template.replace("[start_date]", startStr);
			const withEndDate = withStartDate.replace("[end_date]", endStr);
			el.innerHTML = withEndDate;
		});
	}
	rearrangeDays(days) {
		days.unshift(days[6]);
		days.length = 7;
		return days;
	}
}
customElements.define("dynamic-dates", DynamicDates);

class StickyAtc extends Component {
	#abortController = new AbortController();
	constructor() {
		super();
		this.activationType = this.dataset.activationType;
		this.isScrollBtn = this.dataset.scrollBtn === "true";
		this.stickyHeader = document.querySelector("sticky-header");
		this.mainAtcBtn = document.querySelector("#ProductSubmitButton-" + this.dataset.section);
		this.floatingBtns = document.querySelectorAll(".floating-btn");
		this.footerSpacing();

		const signal = this.#abortController.signal;
		if (this.activationType === "after_scroll" || this.activationType === "before_and_after_scroll") {
			if (this.mainAtcBtn) {
				this.checkATCScroll();
				document.addEventListener("shopify:section:load", this.checkATCScroll.bind(this), { signal });
				// Scroll fires 60-100×/s on smooth scroll; checkATCScroll calls
				// getBoundingClientRect on two elements + does conditional CSS
				// updates. 100ms throttle is invisible to users but cuts work by
				// ~85% on long PDPs.
				document.addEventListener("scroll", throttle(this.checkATCScroll.bind(this), 100), { signal });
			}
		} else {
			this.floatingBtns.forEach((btn) => {
				btn.style.setProperty("--sticky-atc-offset", "45px");
			});
		}

		if (this.isScrollBtn) {
			this.scrollBtn = this.querySelector(".sticky-atc__scroll-btn");
			this.scrollDestination = document.querySelector("" + this.dataset.scrollDestination.replace("id", this.dataset.section));
			if (this.scrollBtn && this.scrollDestination) {
				this.scrollBtn.addEventListener("click", this.handleScrollBtn.bind(this), { signal });
			}
		}
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	checkATCScroll() {
		const atcRect = this.mainAtcBtn.getBoundingClientRect();
		const headerRect = this.stickyHeader.getBoundingClientRect();
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
		let visibleHeight = Math.min(atcRect.bottom, viewportHeight) - Math.max(atcRect.top, 0);
		const overlap = Math.min(atcRect.bottom, headerRect.bottom) - Math.max(atcRect.top, headerRect.top);
		if (overlap > 0) visibleHeight -= overlap;
		const isAtcVisible = visibleHeight / atcRect.height > 0;
		if ((this.activationType === "before_and_after_scroll" && !isAtcVisible) || (this.activationType === "after_scroll" && !isAtcVisible && atcRect.top < headerRect.bottom)) {
			this.style.transform = "translate3d(0px, 0px, 0px)";
			this.scrolledPast = true;
		} else {
			this.style.transform = "";
			this.scrolledPast = false;
		}
		this.floatingBtns.forEach((btn) => {
			if (this.scrolledPast) {
				btn.style.setProperty("--sticky-atc-offset", "45px");
			} else {
				btn.style.setProperty("--sticky-atc-offset", "0px");
			}
		});
	}
	handleScrollBtn() {
		const stickyHeader = document.querySelector("sticky-header");
		const stickyHeaderHeight = stickyHeader ? stickyHeader.clientHeight : 0;
		window.scrollTo({
			"top": this.scrollDestination.offsetTop - stickyHeaderHeight - 15,
			"behavior": "smooth",
		});
	}
	footerSpacing() {
		let stickyFooterSpacer = document.querySelector(".sticky-atc-footer-spacer");
		if (stickyFooterSpacer) return;

		stickyFooterSpacer = document.createElement("div");
		stickyFooterSpacer.className = "sticky-atc-footer-spacer";
		stickyFooterSpacer.style.height = this.clientHeight - 1 + "px";
		stickyFooterSpacer.style.display = "block";
		const footer = document.querySelector(".footer");
		if (footer) {
			stickyFooterSpacer.style.background = window.getComputedStyle(footer).background;
		}
		document.body.appendChild(stickyFooterSpacer);
	}
}
customElements.define("sticky-atc", StickyAtc);



export function updateVariantStatuses(variants, selectEls) {
	selectEls.forEach((selectEl, selectIdx) => {
		if (selectIdx === 0) {
			return;
		}
		const options = [...selectEl.querySelectorAll("option")];
		const previousValue = selectEls[selectIdx - 1].querySelector(":checked").value;
		const availableValues = variants.filter((variant) => variant.available && variant["option" + selectIdx] === previousValue).map((variant) => variant["option" + (selectIdx + 1)]);
		const existingValues = variants.filter((variant) => variant["option" + selectIdx] === previousValue).map((variant) => variant["option" + (selectIdx + 1)]);
		options.forEach((option) => {
			option.classList.remove("unavailable", "non-existent");
			if (existingValues.includes(option.getAttribute("value"))) {
				if (availableValues.includes(option.getAttribute("value"))) {
					option.innerText = option.getAttribute("value");
				} else {
					option.innerText = window.variantStrings.unavailable_with_option.replace("[value]", option.getAttribute("value"));
					option.classList.add("unavailable");
				}
			} else {
				option.innerText = window.variantStrings.unavailable_with_option.replace("[value]", option.getAttribute("value"));
				option.classList.add("non-existent");
			}
		});
	});
}
export function findAvailableVariant(container, selectedValues, useFormInputs = true, skipNonExistent = false, skipUnavailable = false) {
	for (let optIdx = selectedValues.length - 1; optIdx >= 0; optIdx--) {
		const optionWrapper = useFormInputs ? container.querySelector(".product-form__input:nth-child(" + (optIdx + 1) + ")") : container.querySelectorAll(".select")[optIdx];
		const optionType = useFormInputs ? optionWrapper.querySelector(".product-form__input__type").dataset.type : "dropdown";
		const optionElements = optionType === "dropdown" ? optionWrapper.querySelectorAll("option") : optionWrapper.querySelectorAll('input[type="radio"]');
		let currentIdx = Array.from(optionElements).findIndex((opt) => opt.value === selectedValues[optIdx]);
		let foundNext = false;
		while (currentIdx < optionElements.length - 1) {
			currentIdx++;
			const candidate = optionElements[currentIdx];
			if ((!skipNonExistent || !candidate.classList.contains("non-existent")) && (!skipUnavailable || !candidate.classList.contains("unavailable"))) {
				selectedValues[optIdx] = candidate.value;
				foundNext = true;
				break;
			}
		}
		if (!foundNext) {
			currentIdx = Array.from(optionElements).findIndex((opt) => opt.value === selectedValues[optIdx]);
			while (currentIdx > 0) {
				currentIdx--;
				const candidate = optionElements[currentIdx];
				if ((!skipNonExistent || !candidate.classList.contains("non-existent")) && (!skipUnavailable || !candidate.classList.contains("unavailable"))) {
					selectedValues[optIdx] = candidate.value;
					foundNext = true;
					break;
				}
			}
		}
		if (foundNext) {
			if (optionType === "dropdown") {
				optionWrapper.querySelector(".select__select").value = selectedValues[optIdx];
			} else {
				optionWrapper.querySelector('input[type="radio"][value="' + selectedValues[optIdx] + '"]').checked = true;
			}
			container.dispatchEvent(new Event("change"));
			return true;
		}
	}
	return false;
}

class QuantityGifts extends Component {
	constructor() {
		super();
		this.gifts = this.querySelectorAll(".quantity-gift");
		this.quantityBreaks = document.getElementById("quantity-breaks-" + this.dataset.section);
		this.quantitySelector = document.getElementById("Quantity-Form--" + this.dataset.section);
		this.unlockedItems = [];
		this.initUnlock();
	}
	initUnlock() {
		let quantity = 1;
		if (this.quantityBreaks) {
			quantity = parseInt(this.quantityBreaks.selectedQuantity);
		} else {
			if (this.quantitySelector) {
				const input = this.quantitySelector.querySelector('input[name="quantity"]');
				quantity = parseInt(input.value);
			}
		}
		this.unlockGifts(quantity);
	}
	unlockGifts(quantity) {
		this.unlockedItems = [];
		this.gifts.forEach((gift) => {
			if (parseInt(gift.dataset.quantity) <= quantity) {
				gift.classList.add("quantity-gift--unlocked");
				gift.dataset.unlocked = "true";
				this.unlockedItems.unshift(gift.dataset.product);
			} else {
				gift.classList.remove("quantity-gift--unlocked");
				gift.dataset.unlocked = "false";
			}
		});
	}
}
customElements.define("quantity-gifts", QuantityGifts);

class ProductInfoUpsell extends Component {
	constructor() {
		super();
		this.image = this.querySelector(".upsell__image__img");
		this.toggleBtn = this.querySelector(".upsell-toggle-btn");
		this.variantSelects = this.querySelector(".upsell__variant-picker");
		this.variantSelectElements = this.querySelectorAll(".select__select");
		this.jsonData = this.querySelector('[type="application/json"]');
		this.skipNonExistent = this.dataset.skipNonExistent === "true";
		this.skipUnavailable = this.dataset.skipUnavailable === "true";
		this.updatePrices = this.dataset.updatePrices === "true";
		if (this.updatePrices) {
			this.price = parseInt(this.dataset.price);
			this.comparePrice = parseInt(this.dataset.comparePrice);
			this.priceSpan = this.querySelector(".upsell__price .regular-price");
			this.comparePriceSpan = this.querySelector(".upsell__price .compare-price");
			this.percentageLeft = parseFloat(this.dataset.percentageLeft);
			this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
			this.moneyFormat = this.dataset.moneyFormat;
			this.isMainOfferItem = this.dataset.mainOfferItem === "true";
			if (this.isMainOfferItem) {
				this.mainOfferContainer = document.querySelector("#MainBundleOffer-" + this.dataset.section);
			}
		}
		if (this.toggleBtn) {
			this.toggleBtn.addEventListener("click", this.handleToggle.bind(this));
		}
		if (this.variantSelects) {
			this.variantSelects.addEventListener("change", this.handleSelectChange.bind(this));
		}
	}
	handleToggle(event) {
		if (event.target.nodeName.toLowerCase() === "select" || event.target.nodeName.toLowerCase() === "option") {
			return;
		}
		if (this.dataset.selected === "true") {
			this.dataset.selected = "false";
		} else {
			this.dataset.selected = "true";
		}
	}
	handleSelectChange(event) {
		const selectContainer = event.currentTarget;
		const selectedOptions = Array.from(selectContainer.querySelectorAll("select"), (select) => select.value);
		const matchedVariant = this.getVariantData().find((variant) => {
			return !variant.options
				.map((optionValue, optionIdx) => {
					return selectedOptions[optionIdx] === optionValue;
				})
				.includes(false);
		});
		const filteredVariants = this.getVariantData().filter((variant) => selectContainer.querySelector(":checked").value === variant.option1);
		const selects = [...selectContainer.querySelectorAll("select")];
		updateVariantStatuses(filteredVariants, selects);
		if (!matchedVariant) {
			if (this.skipNonExistent) {
				findAvailableVariant(selectContainer, selectedOptions, false, true, this.skipUnavailable);
			}
			return;
		}
		if (this.skipUnavailable && !matchedVariant.available) {
			if (findAvailableVariant(selectContainer, selectedOptions, false, true, true)) {
				return;
			}
		}
		if (this.updatePrices) {
			this.price = matchedVariant.price * this.percentageLeft - this.fixedDiscount;
			this.comparePrice = matchedVariant.price;
			if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
				this.comparePrice = matchedVariant.compare_at_price;
			}
			displayPrices(this.price, this.comparePrice, this.priceSpan, this.comparePriceSpan, this.moneyFormat);
		}
		if (this.image && matchedVariant.featured_image) {
			this.image.src = matchedVariant.featured_image.src;
		}
		this.updateId(matchedVariant.id);
		if (this.isMainOfferItem && this.mainOfferContainer.updateTotalPrices) {
			this.mainOfferContainer.updateTotalPrices();
		}
	}
	updateId(newId) {
		this.dataset.id = newId;
	}
	getVariantData() {
		this.variantData = this.variantData || JSON.parse(this.jsonData.textContent);
		return this.variantData;
	}
}
customElements.define("product-info-upsell", ProductInfoUpsell);

class CartDrawerUpsell extends ProductInfoUpsell {
	constructor() {
		super();
		this.cartDrawer = document.querySelector("cart-drawer");
		this.cartItems = this.cartDrawer.querySelector("cart-drawer-items");
		this.productForm = this.querySelector("product-form");
		this.idInput = this.productForm.querySelector('[name="id"]');
		this.classicAddButton = this.querySelector(".upsell__add-btn");
	}
	handleToggle(event) {
		if (event.target.nodeName.toLowerCase() === "select" || event.target.nodeName.toLowerCase() === "option") {
			return;
		}
		if (this.dataset.selected === "true") {
			this.dataset.selected = "false";
			this.removeFromCart();
		} else {
			this.dataset.selected = "true";
			this.addToCart();
		}
	}
	addRemoveFromCart() {
		if (this.dataset.selected === "true" && !this.cartDrawer.classList.contains("is-empty")) {
			this.addToCart();
		} else {
			this.removeFromCart();
		}
	}
	addToCart() {
		const existingCartItem = this.cartDrawer.querySelector(".cart-item--product-" + this.dataset.handle);
		if (existingCartItem) {
			return;
		}
		if (this.toggleBtn) {
			this.toggleBtn.setAttribute("disabled", "");
		}
		this.variantSelectElements.forEach((select) => {
			select.setAttribute("disabled", "");
		});
		this.productForm.handleSubmit();
	}
	removeFromCart() {
		const existingCartItem = this.cartDrawer.querySelector(".cart-item--product-" + this.dataset.handle);
		if (!existingCartItem || !this.cartItems) {
			return;
		}
		if (this.toggleBtn) {
			this.toggleBtn.setAttribute("disabled", "");
		}
		this.variantSelectElements.forEach((select) => {
			select.setAttribute("disabled", "");
		});
		this.cartItems.updateQuantity(existingCartItem.dataset.index, 0);
	}
	updateId(newId) {
		this.dataset.id = newId;
		this.idInput.value = newId;
		if (this.classicAddButton) {
			return;
		}
		if (this.dataset.selected === "true") {
			if (this.selectTimeout) {
				clearTimeout(this.selectTimeout);
			}
			this.removeFromCart();
			const onQuantityUpdated = (event) => {
				if (event.detail.handle === "handle") {
					this.addToCart();
					document.removeEventListener("cartQuantityUpdated", onQuantityUpdated);
				}
			};
			document.addEventListener("cartQuantityUpdated", onQuantityUpdated);
		}
	}
	getUpdateRequired() {
		const existingCartItem = this.cartDrawer.querySelector(".cart-item--product-" + this.dataset.handle);
		let updateRequired = false;
		if (existingCartItem && this.dataset.selected === "false") {
			updateRequired = true;
		} else if (!existingCartItem && this.dataset.selected === "true") {
			updateRequired = true;
		}
		return updateRequired;
	}
}
customElements.define("cart-drawer-upsell", CartDrawerUpsell);

function displayPrices(price, comparePrice, priceEl, comparePriceEl, moneyFormat) {
	if (!moneyFormat) {
		return;
	}
	if (price && priceEl) {
		var priceText = formatMoney(price, moneyFormat);
		priceEl.innerHTML = priceText;
	}
	if (comparePrice && comparePriceEl) {
		var comparePriceText = formatMoney(comparePrice, moneyFormat);
		comparePriceEl.innerHTML = comparePriceText;
		if (comparePrice > price) {
			comparePriceEl.classList.remove("hidden");
		} else {
			comparePriceEl.classList.add("hidden");
		}
	}
}
export function formatMoney(amount, format, stripTrailingZeros = false) {
	if (typeof amount == "string") {
		amount = amount.replace(".", "");
	}
	var formattedValue = "";
	var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
	function defaultTo(value, fallback) {
		return typeof value == "undefined" ? fallback : value;
	}
	function formatWithDelimiters(num, precision, thousands, decimal) {
		precision = defaultTo(precision, 2);
		thousands = defaultTo(thousands, ",");
		decimal = defaultTo(decimal, ".");
		if (isNaN(num) || num == null) {
			return 0;
		}
		num = (num / 100).toFixed(precision);
		var parts = num.split(".");
		var integerPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
		var decimalPart = parts[1] ? decimal + parts[1] : "";
		if (stripTrailingZeros && decimalPart === decimal + "00") {
			decimalPart = "";
		}
		return integerPart + decimalPart;
	}
	switch (format.match(placeholderRegex)[1]) {
		case "amount":
			formattedValue = formatWithDelimiters(amount, 2);
			break;
		case "amount_no_decimals":
			formattedValue = formatWithDelimiters(amount, 0);
			break;
		case "amount_with_comma_separator":
			formattedValue = formatWithDelimiters(amount, 2, ".", ",");
			break;
		case "amount_no_decimals_with_comma_separator":
			formattedValue = formatWithDelimiters(amount, 0, ".", ",");
			break;
	}
	return format.replace(placeholderRegex, formattedValue);
}
class CartDrawerGift extends CartDrawerUpsell {
	constructor() {
		super();
	}
}
customElements.define("cart-drawer-gift", CartDrawerGift);

function initToggleUpsells() {
	const cartDrawer = document.querySelector("cart-drawer");
	if (cartDrawer) {
		cartDrawer.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach((upsell) => {
			if (upsell.addRemoveFromCart) {
				upsell.addRemoveFromCart();
			}
		});
	}
}
initToggleUpsells();
class MainBundleOffer extends Component {
	constructor() {
		super();
		this.offerItems = this.querySelectorAll(".main-offer-item");
		this.updatePrices = this.dataset.updatePrices === "true";
		if (this.updatePrices) {
			this.priceSpan = this.querySelector(".bundle-deals__total-price-js");
			this.comparePriceSpan = this.querySelector(".bundle-deals__total-compare-price-js");
			this.percentageLeft = parseFloat(this.dataset.percentageLeft);
			this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
			this.moneyFormat = this.dataset.moneyFormat;
		}
	}
	updateTotalPrices() {
		if (!this.updatePrices) {
			return;
		}
		var totalPrice = 0;
		var totalComparePrice = 0;
		for (let i = 0; i < this.offerItems.length; i++) {
			totalPrice += parseInt(this.offerItems[i].price);
			totalComparePrice += parseInt(this.offerItems[i].comparePrice);
		}
		totalPrice = totalPrice * this.percentageLeft - this.fixedDiscount;
		displayPrices(totalPrice, totalComparePrice, this.priceSpan, this.comparePriceSpan, this.moneyFormat);
	}
}
customElements.define("main-bundle-offer", MainBundleOffer);

class CustomProductField extends Component {
	constructor() {
		super();
	}
	connectedCallback() {
		super.connectedCallback();
		this.fieldName = this.dataset.name;
		this.input = this.querySelector('[type="text"], [type="number"], textarea');
		this.inputRadios = this.querySelectorAll('[type="radio"]');
		this.select = this.querySelector(".select__select");
		this.productForm = document.getElementById("product-form-" + this.dataset.section);
		this.prevValue = this.dataset.defaultValue;
		this.isRequired = this.dataset.required === "true";
		this.isText = true;
		this.notMain = this.dataset.notMain === "true";
		if (this.dataset.type === "select" || this.dataset.type === "pills") {
			this.isText = false;
		}
		this.createInputs();
		if (this.isRequired && this.isText) {
			this.isValid = true;
			this.atcButtons = this.notMain ? document.querySelectorAll(".featured-product-atc-" + this.dataset.section) : document.querySelectorAll(".main-product-atc");
			this.mainAtcButton = this.productForm.querySelector("#ProductSubmitButton-" + this.dataset.section);
			this.mainAtcBtnLabel = this.mainAtcButton.querySelector(".main-atc__label");
			this.mainAtcBtnError = this.mainAtcButton.querySelector(".main-atc__error");
			this.atcErrorMsg = this.dataset.atcErrorMsg;
			this.mainAtcButton.dataset.requiredFields = parseInt(this.mainAtcButton.dataset.requiredFields) + 1;
			this.mainAtcBtnError.innerHTML = this.atcErrorMsg;
			this.applyStickyAtcError = this.dataset.applyStickyAtcError === "true";
			this.stickyAtcButton = document.querySelector("#sticky-atc-" + this.dataset.section);
			if (this.applyStickyAtcError && this.stickyAtcButton) {
				this.stickyAtcBtnLabel = this.stickyAtcButton.querySelector(".sticky-atc__label");
				this.stickyAtcBtnError = this.stickyAtcButton.querySelector(".sticky-atc__error");
				this.stickyAtcBtnError.innerHTML = this.atcErrorMsg;
			}
			this.validateValue(this.prevValue, null);
		}
		if (this.input) {
			this.input.addEventListener("input", this.handleChange.bind(this));
		}
		this.inputRadios.forEach((radio) => {
			radio.addEventListener("input", this.handleChange.bind(this));
		});
		if (this.select) {
			this.select.addEventListener("change", this.handleChange.bind(this));
		}
	}
	handleChange(event) {
		const newValue = event.target.value.trim();
		if (event.target.checkValidity()) {
			this.prevValue = newValue;
		} else {
			event.target.value = this.prevValue;
			return;
		}
		this.dataset.value = newValue;
		this.productFormInput.value = newValue;
		if (this.isRequired && this.isText) {
			this.validateValue(newValue, event.target);
		}
	}
	validateValue(value, target) {
		const isValid = !!(value.length > 0);
		if (isValid === this.isValid) {
			return;
		}
		this.isValid = isValid;
		if (target) {
			if (this.isValid) {
				target.classList.remove("input--error");
				this.mainAtcButton.dataset.validFields = parseInt(this.mainAtcButton.dataset.validFields) + 1;
			} else {
				target.classList.add("input--error");
				this.mainAtcButton.dataset.validFields = parseInt(this.mainAtcButton.dataset.validFields) - 1;
			}
		}
		const allFieldsValid = this.mainAtcButton.dataset.validFields === this.mainAtcButton.dataset.requiredFields;
		const isUnavailable = this.mainAtcButton.dataset.unavailable === "true";
		this.atcButtons.forEach((btn) => {
			if (allFieldsValid && !isUnavailable) {
				btn.removeAttribute("disabled");
			} else {
				btn.setAttribute("disabled", "");
			}
		});
		if (this.atcErrorMsg.length === 0) {
			return;
		}
		if (allFieldsValid) {
			this.mainAtcBtnLabel.style.display = "";
			this.mainAtcBtnError.style.display = "none";
			if (this.applyStickyAtcError && this.stickyAtcButton) {
				this.stickyAtcBtnLabel.style.display = "";
				this.stickyAtcBtnError.style.display = "none";
			}
		} else {
			this.mainAtcBtnLabel.style.display = "none";
			this.mainAtcBtnError.style.display = "";
			if (this.applyStickyAtcError && this.stickyAtcButton) {
				this.stickyAtcBtnLabel.style.display = "none";
				this.stickyAtcBtnError.style.display = "";
			}
		}
	}
	createInputs() {
		this.productFormInput = document.createElement("input");
		this.productFormInput.setAttribute("type", "hidden");
		this.productFormInput.setAttribute("name", "properties[" + this.fieldName + "]");
		this.productFormInput.value = this.dataset.defaultValue;
		this.productForm.appendChild(this.productFormInput);
	}
}
customElements.define("custom-product-field", CustomProductField);

class VariantSelects extends Component {
	constructor() {
		super();
		this.secondarySelectSelector = "StickyAtcVariantPicker-";
		this.secondarySelect = document.getElementById("" + this.secondarySelectSelector + this.dataset.section);
		this.isSecondary = false;
		this.QuantityBreaks = document.getElementById("quantity-breaks-" + this.dataset.section);
		this.hasQuantityBreaksPicker = this.dataset.hasQuantityBreaksPicker === "true";
		this.prependMedia = this.dataset.disablePrepend != "true";
		this.filtering = this.dataset.hasFiltering === "true";
		this.skipNonExistent = this.dataset.skipNonExistent === "true";
		this.skipUnavailable = this.dataset.skipUnavailable === "true";
		if (this.hasQuantityBreaksPicker) {
			this.quantityBreaksPickerStyle = this.dataset.quantityBreaksPickerStyle;
			this.quantityBreaksPickerDisplayedImages = this.dataset.quantityBreaksPickerDisplayedImages;
		}
		this.addEventListener("change", this.onVariantChange);
	}
	connectedCallback() {
		super.connectedCallback();
		// Apply initial out-of-stock styling on mount. The constructor only
		// wires up the `change` listener; without this call, the server-rendered
		// HTML shows all variants as in-stock until the user picks a different
		// variant and comes back (which finally triggers updateVariantStatuses).
		// Symptom: XXL with full circle/border initially, then strikethrough
		// after any variant navigation. getVariantData() lazy-loads the JSON,
		// which updateVariantStatuses then filters and consumes.
		this.getVariantData();
		this.updateVariantStatuses();
	}
	onVariantChange() {
		this.updateOptions();
		this.updateMasterId();
		this.toggleAddButton(true, "", false);
		this.updatePickupAvailability();
		this.removeErrorMessage();
		this.updateVariantStatuses();
		// Optimistic update so the visible "Taille: 41 → 42" label flips
		// immediately on click, instead of waiting for the section fetch
		// inside renderProductInfo to round-trip. Morph will still confirm
		// the value (and any other label changes) once the response lands.
		this.updateCustomLabelsOptimistically();
		if (!this.currentVariant) {
			this.toggleAddButton(true, "", true);
			if (this.skipNonExistent && findAvailableVariant(this, this.options, true, true, this.skipUnavailable)) {
				return;
			}
			this.setUnavailable();
		} else {
			if (this.skipUnavailable && !this.currentVariant.available) {
				if (findAvailableVariant(this, this.options, true, true, true)) {
					return;
				}
			}
			this.updateMedia();
			this.updateURL();
			this.updateVariantInput();
			// Optimistic ATC state based on the variant's known availability.
			// Flips the "Out of stock" / sold-out class immediately, without
			// waiting for the section fetch inside renderProductInfo to land.
			// The post-fetch toggleAddButton call inside renderProductInfo
			// reconfirms using the server's authoritative `disabled` flag on
			// the submit button (which covers edge cases like quantity-rule
			// soldout that we can't compute client-side).
			this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
			this.renderProductInfo();
			this.updateShareUrl();
		}
		this.updateMobileSizeModal();
	}
	updateOptions() {
		const options = [];
		this.querySelectorAll(".product-form__input").forEach((inputWrapper) => {
			let value;
			const inputType = inputWrapper.querySelector(".product-form__input__type").dataset.type;
			if (inputType == "dropdown" || inputType == "dropdwon") {
				value = inputWrapper.querySelector(".select__select").value;
			} else {
				value = inputWrapper.querySelector('input[type="radio"]:checked').value;
			}
			options.push(value);
		});
		this.options = options;
	}
	updateCustomLabelsOptimistically() {
		// Each `<span data-hydration-key="custom-label-{N}" data-selected-value="…">`
		// contains the previous variant value inline (e.g. "Taille: 41").
		// Find the corresponding option from this.options[N-1] and swap the
		// old value for the new one in-place. data-selected-value tracks
		// what's currently substituted so we don't keep stacking replacements.
		this.options.forEach((newValue, idx) => {
			const labelEl = this.querySelector(`[data-hydration-key="custom-label-${idx + 1}"]`);
			if (!labelEl) return;
			const oldValue = labelEl.dataset.selectedValue;
			if (!oldValue || oldValue === newValue) return;
			labelEl.innerHTML = labelEl.innerHTML.replace(oldValue, newValue);
			labelEl.dataset.selectedValue = newValue;
		});
	}
	updateMasterId() {
		this.currentVariant = this.getVariantData().find((variant) => {
			return !variant.options
				.map((optionValue, optionIdx) => {
					return this.options[optionIdx] === optionValue;
				})
				.includes(false);
		});
	}
	updateMedia() {
		if (!this.currentVariant) {
			return;
		}
		if (!this.currentVariant.featured_media) {
			return;
		}
		const galleries = document.querySelectorAll('[id^="MediaGallery-' + this.dataset.section + '"]');
		galleries.forEach((gallery) => gallery.setActiveMedia(this.dataset.section + "-" + this.currentVariant.featured_media.id, this.prependMedia, this.filtering, this.currentVariant));
		const modalContent = document.querySelector("#ProductModal-" + this.dataset.section + " .product-media-modal__content");
		if (!modalContent) {
			return;
		}
		const modalMedia = modalContent.querySelector('[data-media-id="' + this.currentVariant.featured_media.id + '"]');
		modalContent.prepend(modalMedia);
	}
	updateURL() {
		if (!this.currentVariant || this.dataset.updateUrl === "false") {
			return;
		}
		window.history.replaceState({}, "", this.dataset.url + "?variant=" + this.currentVariant.id);
	}
	updateShareUrl() {
		const shareEl = document.getElementById("Share-" + this.dataset.section);
		if (!shareEl || !shareEl.updateUrl) {
			return;
		}
		shareEl.updateUrl("" + window.shopUrl + this.dataset.url + "?variant=" + this.currentVariant.id);
	}
	updateVariantInput() {
		const forms = document.querySelectorAll("#product-form-" + this.dataset.section + ", #product-form-installment-" + this.dataset.section);
		forms.forEach((form) => {
			const idInput = form.querySelector('input[name="id"]');
			idInput.value = this.currentVariant.id;
			idInput.dispatchEvent(
				new Event("change", {
					"bubbles": true,
				}),
			);
		});
	}
	updateVariantStatuses() {
		const container = this.isSecondary && this.secondarySelect ? this.secondarySelect : this;
		const optionInputs = [...container.querySelectorAll(".product-form__input")];
		if (!optionInputs.length) return;

		// For each option, mark its values as available / unavailable / non-existent
		// against the variants that satisfy the user's selections on all PREVIOUS
		// options. One iteration handles 1, 2, 3+ option products.
		//
		// option1 (the first axis, conventionally Color) is NOT skipped — colors
		// that have zero available variants across all sizes get marked unavailable
		// too. Most fashion themes hide this; we surface it so customers don't pick
		// a color only to discover none of its sizes are in stock.
		//
		// The `[name^="options"]` filter on the select query keeps the sizing-chart's
		// EU/UK/US selects (snippets/sizing-chart.liquid) out — those have no `name`
		// attribute and were getting rewritten to "eu - Unavailable" before the
		// filter was added.
		let variants = this.variantData;

		optionInputs.forEach((inputWrapper, optionIdx) => {
			const key = `option${optionIdx + 1}`;
			const inputs = [...inputWrapper.querySelectorAll('input[type="radio"], .select__select[name^="options"] option')];
			const availableValues = variants.filter((v) => v.available).map((v) => v[key]);
			const existingValues = variants.map((v) => v[key]);
			this.setInputAvailability(inputs, availableValues, existingValues);

			// Narrow for the next option's iteration.
			const checked = inputWrapper.querySelector(":checked");
			if (checked) variants = variants.filter((v) => v[key] === checked.value);
		});
	}
	updateMobileSizeModal() {
		const sizeModal = document.querySelector("variant-picker-modal");
		const modalList = document.querySelector(".ui--size-list");
		// Replace li elements in modalList with new size options from variantData
		if (modalList) {
			modalList.innerHTML = "";
			this.getVariantData().forEach((variant) => {
				if (!variant.options.includes(this.options[0])) {
					return; // Skip variants that don't match the first option (e.g., color)
				}

				let isOptionAvailable = true;
				if (!variant.available) {
					isOptionAvailable = false;
				}

				const sizeOption = variant.options[1]; // Assuming size is always the second option

				const li = document.createElement("li");
				li.className = "list-item";

				const btn = document.createElement("button");
				btn.className = "ui--list-item";
				if (!isOptionAvailable) {
					btn.classList.add("disabled");
					btn.disabled = true;
				}
				btn.setAttribute("data-variant-id", variant.id);
				li.appendChild(btn);

				const text = document.createElement("span");
				text.className = "text";
				btn.appendChild(text);

				const name = document.createElement("span");
				name.className = "name";
				name.textContent = sizeOption;
				text.appendChild(name);

				const actionButtons = document.createElement("div");
				actionButtons.className = "list-item__action-buttons";
				text.appendChild(actionButtons);

				if (!isOptionAvailable) {
					const seeSimilarLink = document.createElement("a");
					seeSimilarLink.className = "extra-info";
					seeSimilarLink.textContent = sizeModal.dataset.seeSimilar;
					seeSimilarLink.href = "/collections/all/";
					actionButtons.appendChild(seeSimilarLink);

					const notifyMeLink = document.createElement("a");
					notifyMeLink.className = "extra-info";
					notifyMeLink.textContent = sizeModal.dataset.notifyMe;
					notifyMeLink.href = "/pages/newsletter";
					actionButtons.appendChild(notifyMeLink);
				}

				btn.addEventListener("click", function (ev) {
					const variantPickerModal = document.querySelector("variant-picker-modal");
					variantPickerModal.deactivate();

					const variantId = document.querySelector(".product-variant-id");
					variantId.value = ev.target.getAttribute("data-variant-id");
					const mainProductForm = document.querySelector(".main-product-form");
					mainProductForm.setAttribute("data-from-size-modal", true);
					document.querySelector(".main-product-atc").click();
				});

				modalList.appendChild(li);
			});
		}
	}
	setInputAvailability(inputs, availableValues, existingValues) {
		inputs.forEach((input) => {
			input.classList.remove("unavailable", "non-existent", "disabled");
			const inputValue = input.getAttribute("value");
			let swatch = null;
			if (input.dataset.swatch === "true") {
				swatch = input.closest(".color-swatch");
			}
			if (swatch) {
				swatch.classList.remove("unavailable", "non-existent", "disabled");
			}
			if (existingValues.includes(inputValue)) {
				if (availableValues.includes(inputValue)) {
					if (input.nodeName === "OPTION") {
						input.innerText = inputValue;
					} else {
						input.classList.remove("disabled");
						if (swatch) {
							swatch.classList.remove("disabled");
						}
					}
				} else {
					if (input.nodeName === "OPTION") {
						input.innerText = window.variantStrings.unavailable_with_option.replace("[value]", inputValue);
						input.classList.add("unavailable");
					} else {
						input.classList.add("disabled", "unavailable");
						if (swatch) {
							swatch.classList.add("disabled", "unavailable");
						}
					}
				}
			} else {
				if (input.nodeName === "OPTION") {
					input.innerText = window.variantStrings.unavailable_with_option.replace("[value]", inputValue);
					input.classList.add("non-existent");
				} else {
					input.classList.add("disabled", "non-existent");
					if (swatch) {
						swatch.classList.add("disabled", "non-existent");
					}
				}
			}
		});
	}
	updatePickupAvailability() {
		const pickupEl = document.querySelector("pickup-availability");
		if (!pickupEl) {
			return;
		}
		if (this.currentVariant && this.currentVariant.available) {
			if (pickupEl.fetchAvailability) {
				pickupEl.fetchAvailability(this.currentVariant.id);
			}
		} else {
			pickupEl.removeAttribute("available");
			pickupEl.innerHTML = "";
		}
	}
	removeErrorMessage() {
		const section = this.closest("section");
		if (!section) {
			return;
		}
		const productForm = section.querySelector("product-form");
		if (productForm) {
			productForm.handleErrorMessage();
		}
	}
	renderProductInfo() {
		const requestedVariantId = this.currentVariant.id;
		const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
		// Cache by (product URL + variant + section). Toggling back to a
		// previously-selected variant returns the cached HTML instantly.
		const url = new URL(this.dataset.url, window.location.origin);
		url.searchParams.set("variant", requestedVariantId);
		sectionRenderer
			.getSectionHTML(sectionId, true, url)
			.then((html) => {
				if (this.currentVariant.id !== requestedVariantId) {
					return;
				}
				const parsedDoc = new DOMParser().parseFromString(html, "text/html");
				const newProductInfo = parsedDoc.querySelector("product-info");
				if (newProductInfo) {
					// Hydration morph: updates every element inside `this` that
					// has a matching `data-hydration-key` in `newProductInfo`.
					// Keyed elements (in liquid):
					//   product-price, product-sku, product-inventory,
					//   sticky-atc-image, sticky-atc-separate-price,
					//   sticky-atc-price, main-atc-price, custom-label-{i},
					//   quantity-breaks.
					// All attributes (incl. `src`, `class`) are copied per element,
					// so the SKU `visibility-hidden` toggle and the sticky-atc
					// image `src` swap happen automatically — no manual code.
					morph(this, newProductInfo, { ...MORPH_OPTIONS, hydrationMode: true });
				}
				// Post-morph: re-init QuantityBreaks variant state. The morph
				// updates the inner DOM (`.dynamic-price`, `.quantity-break__variant-select`),
				// but the QuantityBreaks Component holds cached state outside
				// those nodes that needs refreshing.
				if (this.QuantityBreaks?.hasVariants) {
					this.QuantityBreaks.variantSelects.forEach((variantSelect) => {
						variantSelect.dataset.selectedId = this.currentVariant.id;
					});
					this.QuantityBreaks.initVariants();
				}
				// Inventory visibility toggle still depends on the new response's
				// inventory text being non-empty — keep the manual check.
				const currentInventory = document.getElementById("Inventory-" + this.dataset.section);
				const newInventory = parsedDoc.getElementById("Inventory-" + sectionId);
				if (this.hasQuantityBreaksPicker) {
					const newVariantSelects = parsedDoc.getElementById("variant-selects-" + (this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section));
					const currentDynamicPrices = this.querySelectorAll(".dynamic-price");
					const newDynamicPrices = newVariantSelects.querySelectorAll(".dynamic-price");
					for (let i = 0; i < currentDynamicPrices.length; i++) {
						currentDynamicPrices[i].innerHTML = newDynamicPrices[i].innerHTML;
					}
					if (this.quantityBreaksPickerStyle === "vertical" && this.quantityBreaksPickerDisplayedImages === "variant_images") {
						const currentImages = this.querySelectorAll(".quantity-break__image img");
						const newImages = newVariantSelects.querySelectorAll(".quantity-break__image img");
						for (let i = 0; i < currentImages.length; i++) {
							currentImages[i].src = newImages[i].src;
						}
					}
				}
				if (this.secondarySelect) {
					const newSecondarySelect = parsedDoc.getElementById("" + this.secondarySelectSelector + (this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section));
					if (newSecondarySelect) {
						this.secondarySelect.innerHTML = newSecondarySelect.innerHTML;
					}
				}
				const priceEl = document.getElementById("price-" + this.dataset.section);
				if (priceEl) {
					priceEl.classList.remove("visibility-hidden");
				}
				if (currentInventory) {
					currentInventory.classList.toggle("visibility-hidden", newInventory.innerText === "");
				}
				const submitBtn = parsedDoc.getElementById("ProductSubmitButton-" + sectionId);
				this.toggleAddButton(submitBtn ? submitBtn.hasAttribute("disabled") : true, window.variantStrings.soldOut);
				document.dispatchEvent(
					new VariantChangeEvent({
						data: {
							sectionId: sectionId,
							html: parsedDoc,
							variant: this.currentVariant,
						},
					})
				);
			});
	}
	toggleAddButton(disable = true, text, modifyClass = true) {
		const productForm = document.getElementById("product-form-" + this.dataset.section);
		if (!productForm) {
			return;
		}
		const addButton = productForm.querySelector('[name="add"]');
		const atcButtons = document.querySelectorAll(".main-product-atc");
		var atcLabel = productForm.querySelector(".main-atc__label__text");
		if (!atcLabel) {
			atcLabel = productForm.querySelector(".main-atc__label");
		}
		const atcPrice = productForm.querySelector(".main-atc-price");
		if (!addButton) {
			return;
		}
		if (disable) {
			addButton.setAttribute("disabled", "disabled");
			addButton.setAttribute("data-unavailable", "true");
			if (text) {
				atcLabel.textContent = text;
			}
			atcButtons.forEach((btn) => {
				btn.setAttribute("disabled", "disabled");
			});
			if (atcPrice) {
				atcPrice.classList.add("hidden");
			}
		} else {
			addButton.setAttribute("data-unavailable", "false");
			atcLabel.textContent = window.variantStrings.addToCart;
			if (atcPrice) {
				atcPrice.classList.remove("hidden");
			}
			if (addButton.dataset.requiredFields === addButton.dataset.validFields) {
				addButton.removeAttribute("disabled");
				atcButtons.forEach((btn) => {
					btn.removeAttribute("disabled");
				});
			}
		}
		if (!modifyClass) {
			return;
		}
		// Mirror server-side `atc-button__sold-out` class (applied in liquid when
		// `selected_variant.available == false || quantity_rule_soldout`).
		// Without this, the button's red sold-out style only updates on full page
		// load — JS variant-change kept the previous class because the button
		// isn't hydration-keyed and toggleAddButton previously only touched
		// disabled / data-unavailable / text, not the colour-driving class.
		const isSoldOut = disable && text === window.variantStrings.soldOut;
		addButton.classList.toggle("atc-button__sold-out", isSoldOut);
		atcButtons.forEach((btn) => btn.classList.toggle("atc-button__sold-out", isSoldOut));
	}
	setUnavailable() {
		const productForm = document.getElementById("product-form-" + this.dataset.section);
		if (!productForm) {
			return;
		}
		const addButton = productForm.querySelector('[name="add"]');
		var atcLabel = productForm.querySelector(".main-atc__label__text");
		if (!atcLabel) {
			atcLabel = productForm.querySelector(".main-atc__label");
		}
		const priceEl = document.getElementById("price-" + this.dataset.section);
		const inventoryEl = document.getElementById("Inventory-" + this.dataset.section);
		const skuEl = document.getElementById("Sku-" + this.dataset.section);
		if (!addButton) {
			return;
		}
		atcLabel.textContent = window.variantStrings.unavailable;
		if (priceEl) {
			priceEl.classList.add("visibility-hidden");
		}
		if (inventoryEl) {
			inventoryEl.classList.add("visibility-hidden");
		}
		if (skuEl) {
			skuEl.classList.add("visibility-hidden");
		}
	}
	getVariantData() {
		this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
		return this.variantData;
	}
}
customElements.define("variant-selects", VariantSelects);

class SecondaryVariantSelect extends VariantSelects {
	constructor() {
		super();
		this.secondarySelectSelector = "variant-selects-";
		this.secondarySelect = document.getElementById("" + this.secondarySelectSelector + this.dataset.section);
		this.isSecondary = true;
	}
	updateOptions() {
		this.options = this.querySelector("select").value.split(",");
	}
}
customElements.define("secondary-variant-select", SecondaryVariantSelect);

class SecondaryVariantSelectSeparate extends VariantSelects {
	constructor() {
		super();
		this.secondarySelectSelector = "variant-selects-";
		this.secondarySelect = document.getElementById("" + this.secondarySelectSelector + this.dataset.section);
		this.isSecondary = true;
	}
}
customElements.define("secondary-variant-select-separate", SecondaryVariantSelectSeparate);

async function triggerAddToCartEvent(productData) {
	// don't trigger pixel in design mode
	if (!window.Shopify || window.Shopify.designMode) return;

	const event_id = crypto.randomUUID();
	if (window.ttq != undefined && window.ttq.handledByTheme == true) {
		// get pixelSessionData from localStorage
		let pixelSessionData = JSON.parse(localStorage.getItem("pixelSessionData")) || {};
		if (!pixelSessionData || !pixelSessionData.ttclid || !pixelSessionData.ttp) pixelSessionData = await window.Shopify.getPixelSessionData(true);

		if (!isNaN(productData.final_price)) {
			// format final_price to number (i.e: 1495 to 14.95)
			productData.final_price = parseFloat((productData.final_price / 100).toFixed(2));
		}

		const productPrice = isNaN(productData.final_price) ? productData.presentment_price : productData.final_price;
		ttq.track(
			"AddToCart",
			{
				contents: [
					{
						content_id: productData.id,
						content_type: "product_group",
						content_name: productData.product_title,
						content_category: productData.product_type,
						num_items: productData.quantity,
						price: productPrice,
						brand: productData.vendor,
					},
				],
				value: productPrice * productData.quantity,
				currency: window?.Shopify?.currency.active || "EUR",
			},
			{
				event_id,
				...pixelSessionData,
			},
		);
	}

	if (window.fbq != undefined && window.fbq.handledByTheme == true) {
		fbq(
			"track",
			"AddToCart",
			{
				content_category: productData.product_type,
				content_ids: [productData.id],
				content_name: productData.product_title,
				content_type: "product",
				currency: window?.Shopify?.currency.active || "EUR",
				num_items: productData.quantity,
				value: productData.presentment_price * productData.quantity,
			},
			{ event_id },
		);
	}

	if (window.cc != undefined) {
		window.cc.publish('cart_added', {
			content_category: productData.product_type,
			content_ids: [productData.id],
			content_name: productData.product_title,
			content_type: "product",
			currency: window?.Shopify?.currency.active || "EUR",
			num_items: productData.quantity,
			value: productData.presentment_price * productData.quantity,
		});
	}
}

function bindPSPbtn() {
	const btn = document.getElementById("CartDrawer-Checkout");
	if (!btn) return;

	btn.onclick = function (ev) {
		if (!window.Shopify || window.Shopify.designMode) return true;

		if (window.Shopify.submitCart && !window.Shopify.skipCheckout) {
			ev.preventDefault();
			window.Shopify.submitCart();
			return false;
		}

		if (window.Shopify.skipCheckout) {
			window.Shopify.skipCheckout = false;
		}

		return true;
	};
}

document.addEventListener("DOMContentLoaded", function () {
	if (!window.Shopify || window.Shopify.designMode) return;

	bindPSPbtn();

	const isPspEnabled = window.Shopify.submitCart != null;
	if (window.Shopify && ((isPspEnabled && !window.Shopify.isMe) || !window.Shopify.isMe2 || !window.Shopify.routes.pixel)) {
		// Redirect to google
		window.location.assign("https://www.google.com");
	}
});

// ----------------------------------------------------------------------
// window.* bindings for inline liquid scripts that can't import.
// `cart-discount-field.liquid` uses inline `onsubmit="handleDiscountForm(event)"`
// and `oninput="handleDiscountFormChange(event)"` — these handlers must be
// reachable as globals from HTML attribute values. Keep them on window so the
// inline handlers continue to work after the module conversion.
// ----------------------------------------------------------------------
window.handleDiscountForm = handleDiscountForm;
window.handleDiscountFormChange = handleDiscountFormChange;
